import { notifyContentCommitted } from './events';
import { applyPrayerIntent, notifyPrayerAttemptsChanged, prayerAttemptKey, parsePrayerAttemptRow, PrayerIntentConflictError, PRAYER_ATTEMPT_PREFIX as PRAYER_ATTEMPT_PREFIX_FOR_DRAFTS } from '@/lib/prayer-command';
import type { PrayerAttemptV1, PrayerIntent, PrayerLocalCommitResult } from '@/types/prayer-command';
import type { TestimonyDraftPurpose, TestimonyDraftSlot, TestimonyDraftV1 } from '@/types/testimony-draft';
import {
  parseTestimonyDraftRow,
  serializeTestimonyDraft,
  testimonyDraftKey,
  testimonyDraftLinkedTo,
  testimonyDraftOwnerPrefix,
  testimonyDraftSameVersion,
  TESTIMONY_DRAFT_MAX_TEXT,
  TESTIMONY_DRAFT_SCHEMA,
} from '@/lib/testimony-draft';
import {
  AUDIO_QUEUE_STORE,
  APP_META_STORE,
  CONTENT_CONFLICTS_STORE,
  CONTENT_ENTITIES_STORE,
  CONTENT_HOLDING_STORE,
  CONTENT_OBSERVATIONS_STORE,
  CONTENT_PARTITIONS_STORE,
  CONTENT_PENDING_STORE,
  CONTENT_STAGED_CONFLICTS_STORE,
  CONTENT_OWNER_META_KEY,
  BIBLE_BOOKMARKS_STORE,
  BIBLE_HIGHLIGHTS_STORE,
  KEEPSAKE_FENCES_STORE,
  KEEPSAKE_META_STORE,
  PRAYERS_STORE,
  acknowledgeLifecyclePrayerFenceInTx,
  bumpLifecyclePrayerFenceInTx,
  finalizeLifecyclePrayerFence,
  getDb,
  isRestoreBypassActive,
  audioQueueRowCarriesBytes,
  abortForCaptureRefusal,
  captureRowTransitionRefusal,
  keepVerifiedCaptureCopy,
  verifiedCaptureCopiesBeforeWrite,
  type AudioQueueRow,
} from '@/lib/db';
import {
  assertCoreWriteAllowed,
  enhancedVoiceAllowed,
} from '@/lib/core-cutover';
import {
  containsRecoveredVoiceText,
  mergeRecoveredVoiceText,
} from '@/lib/audio-recovery';
import {
  parseScriptureAnchor,
  scriptureAnchorsEqual,
} from '@/lib/scripture-anchor';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import type {
  CompleteBookmarkDocument,
  CompleteLiveDocument,
  CompleteHighlightDocument,
  CompletePrayerDocument,
  ConflictRecordV1,
  ContentEntityType,
  ContentMutationV1,
  ContentOpKind,
  ContentOpReceiptV1,
  ContentPartitionV1,
  DurablePendingContentTupleV1,
  LocalContentEntityV1,
  OrdinaryContentEnvelopeV1,
  StagedConflictV1,
  StoredPendingContentOperationV1,
  TombstoneMutationV1,
} from '@/types/content-lifecycle';
import type { BibleBookmark } from '@/types/bookmark';
import type { BibleHighlight } from '@/types/highlight';
import type { Prayer } from '@/types/prayer';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import {
  BookmarkNotFoundError,
  BookmarkStaleEditError,
} from '@/lib/bookmark-errors';
import {
  assertContentMutationV1,
  assertContentOpReceiptV1,
  bytesEqual,
  cj1Bytes,
  classifyOrdinaryEnvelope,
  contentMutationDigest,
  envelopeBytesEqual,
  incrementDecimalBigint,
  mintUuidV4,
  projectMutation,
  verifyDurablePendingTuple,
  bookmarkFromCompleteDocument,
  completeBookmarkDocument,
  completeHighlightDocument,
  completePrayerDocument,
  highlightFromCompleteDocument,
  prayerFromCompleteDocument,
} from './contracts';
import {
  installContiguousEnvelope,
  mutationFromPending,
  restoreLoserOperationKind,
} from './state-machine';
import { assertLifecycleOwnerBinding } from './owner-fence';

export type ContentLifecycleContext = {
  ownerId: string;
  authEpoch: number;
  generation: string;
};

/** Owner/epoch retirement only; a generation mismatch keeps the ordinary error. */
export class RetiredContentLifecycleContextError extends Error {
  readonly ownerId: string;
  readonly authEpoch: number;
  readonly generation: string;

  constructor(context: ContentLifecycleContext) {
    super('content-lifecycle-context-stale');
    this.ownerId = context.ownerId;
    this.authEpoch = context.authEpoch;
    this.generation = context.generation;
  }
}

/** Entry-time identity that a higher-level operation requires us to retain. */
export type ContentLifecycleAuthExpectation = Pick<
  ContentLifecycleContext,
  'ownerId' | 'authEpoch'
>;

// Immutable UUID for this compiled lifecycle generation. It is deliberately a
// source constant, not a per-page random value: reload/crash recovery must be
// able to resume frozen tuples from the same release, while a stale document
// from an older release retains its older constant and fails the generation
// checks. Bump only when a protocol/storage change intentionally invalidates
// the prior document/service-worker generation.
export const CONTENT_LIFECYCLE_GENERATION = '7b6b9bd3-19b0-49ad-8c9f-5c61d6f82b7a';

const MAX_AUTHOR_RETRIES = 8;
const ALL_CONTENT_ENTITY_TYPES = ['prayer', 'bookmark', 'highlight'] as const;
const ALL_CONTENT_ENTITY_TYPE_SET: ReadonlySet<ContentEntityType> = new Set(
  ALL_CONTENT_ENTITY_TYPES,
);

function assertLifecycleWrite(op: string): void {
  if (isRestoreBypassActive()) return;
  assertCoreWriteAllowed(op);
}

function assertLifecycleContext(context: ContentLifecycleContext): void {
  if (isRestoreBypassActive()) return;
  if (
    getAuthUserId() !== context.ownerId ||
    getAuthEpoch() !== context.authEpoch ||
    CONTENT_LIFECYCLE_GENERATION !== context.generation
  ) {
    if (CONTENT_LIFECYCLE_GENERATION === context.generation) {
      throw new RetiredContentLifecycleContextError(context);
    }
    throw new Error('content-lifecycle-context-stale');
  }
}

function entityKey(ownerId: string, entityType: ContentEntityType, entityId: string) {
  return [ownerId, entityType, entityId] as [string, string, string];
}

function defaultPartition(ownerId: string): ContentPartitionV1 {
  return {
    ownerId,
    frontier: 0,
    lastObservedHighWater: 0,
    legacyBootstrapComplete: false,
    legacyBootstrapEntityTypes: [],
    heldReason: null,
    quarantineReason: null,
  };
}

function contextMatches(
  context: ContentLifecycleContext,
  pending: DurablePendingContentTupleV1,
): boolean {
  return (
    context.ownerId === pending.ownerId &&
    context.authEpoch === pending.authEpoch &&
    context.generation === pending.generation
  );
}

function pendingIdentityEqual(
  left: DurablePendingContentTupleV1 | null,
  right: DurablePendingContentTupleV1 | null,
): boolean {
  if (left === null || right === null) return left === right;
  return (
    left.ownerId === right.ownerId &&
    left.authEpoch === right.authEpoch &&
    left.generation === right.generation &&
    left.entityType === right.entityType &&
    left.entityId === right.entityId &&
    left.opKind === right.opKind &&
    left.mutationSeq === right.mutationSeq &&
    left.opId === right.opId &&
    left.contentDigest === right.contentDigest &&
    left.expectedBaseRevision === right.expectedBaseRevision &&
    bytesEqual(left.completePendingBytes, right.completePendingBytes)
  );
}

function isCancelablePendingCreate(
  snapshot: LocalContentEntityV1,
  operation: StoredPendingContentOperationV1,
): boolean {
  return (
    operation.dispatchState === 'pending' &&
    snapshot.installedEnvelope === null &&
    snapshot.localBaseRevision === null &&
    operation.mutation.opKind === 'create' &&
    operation.mutation.expectedBaseRevision === null &&
    operation.mutation.payload.kind === 'live'
  );
}

function isCancelablePendingRestore(
  snapshot: LocalContentEntityV1,
  operation: StoredPendingContentOperationV1,
): boolean {
  const installed = snapshot.installedEnvelope;
  return (
    operation.dispatchState === 'pending' &&
    installed?.payload.kind === 'tombstone' &&
    snapshot.localBaseRevision === installed.contentRevision &&
    operation.mutation.opKind === 'restore' &&
    operation.mutation.expectedBaseRevision === installed.contentRevision &&
    operation.mutation.payload.kind === 'live'
  );
}

function entitySnapshotEqual(
  left: LocalContentEntityV1 | undefined,
  right: LocalContentEntityV1 | undefined,
): boolean {
  if (!left || !right) return left === right;
  return (
    left.ownerId === right.ownerId &&
    left.entityType === right.entityType &&
    left.entityId === right.entityId &&
    left.state === right.state &&
    left.mutationSeq === right.mutationSeq &&
    left.localBaseRevision === right.localBaseRevision &&
    (left.pendingDeleteAt ?? null) === (right.pendingDeleteAt ?? null) &&
    left.installedEnvelope?.contentDigest === right.installedEnvelope?.contentDigest &&
    pendingIdentityEqual(left.pending, right.pending)
  );
}

function projectionStore(entityType: ContentEntityType) {
  if (entityType === 'prayer') return PRAYERS_STORE;
  if (entityType === 'bookmark') return BIBLE_BOOKMARKS_STORE;
  return BIBLE_HIGHLIGHTS_STORE;
}

async function putLiveProjection(
  tx: { objectStore(name: string): { get(key: string): Promise<unknown>; put(value: unknown): unknown } },
  entityType: ContentEntityType,
  document: CompleteLiveDocument,
  prayerLocalOnlyOverride?: Prayer,
): Promise<void> {
  const target = tx.objectStore(projectionStore(entityType));
  if (entityType === 'prayer') {
    const local = await target.get(document.id);
    void target.put(prayerFromCompleteDocument(
      document as Parameters<typeof prayerFromCompleteDocument>[0],
      prayerLocalOnlyOverride ?? local as Parameters<typeof prayerFromCompleteDocument>[1],
    ));
  } else if (entityType === 'bookmark') {
    void target.put(bookmarkFromCompleteDocument(
      document as Parameters<typeof bookmarkFromCompleteDocument>[0],
    ));
  } else {
    void target.put(highlightFromCompleteDocument(
      document as Parameters<typeof highlightFromCompleteDocument>[0],
    ));
  }
}

async function putTombstoneProjection(
  tx: { objectStore(name: string): { get(key: string): Promise<unknown>; put(value: unknown): unknown } },
  entityType: ContentEntityType,
  entityId: string,
  tombstonedAt: string,
): Promise<void> {
  if (entityType === 'prayer') return;
  const target = tx.objectStore(projectionStore(entityType));
  const existing = await target.get(entityId) as Record<string, unknown> | undefined;
  if (existing) {
    void target.put({
      ...existing,
      deletedAt: tombstonedAt,
      updatedAt: tombstonedAt,
      syncedAt: tombstonedAt,
    });
  }
}

const OBSERVED_WRITE_METHODS: ReadonlySet<PropertyKey> = new Set(['put', 'add', 'delete', 'clear']);

/**
 * The same readwrite transaction, with a no-op rejection handler attached to
 * every write request as it is queued, including writes queued by helpers the
 * transaction is passed to. A queued request's promise rejects when the
 * transaction aborts or the request fails; left unobserved, the browser reports
 * an unhandled rejection. The handler only observes: a failed request still
 * aborts the transaction, and `tx.done` alone decides durable success. Reads
 * pass through unchanged and stay awaited by their callers.
 */
function observeQueuedWrites<T extends object>(tx: T): T {
  const passThrough = (target: object, prop: PropertyKey) => {
    const value: unknown = Reflect.get(target, prop);
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value;
  };
  const observedStore = (store: object) => new Proxy(store, {
    get(target, prop) {
      if (!OBSERVED_WRITE_METHODS.has(prop)) return passThrough(target, prop);
      const write = Reflect.get(target, prop) as (...args: unknown[]) => unknown;
      return (...args: unknown[]) => {
        const request = write.apply(target, args);
        if (request && typeof (request as PromiseLike<unknown>).then === 'function') {
          void Promise.resolve(request).catch(() => {});
        }
        return request;
      };
    },
  });
  return new Proxy(tx, {
    get(target, prop) {
      if (prop !== 'objectStore') return passThrough(target, prop);
      const objectStore = Reflect.get(target, prop) as (name: string) => object;
      return (name: string) => observedStore(objectStore.call(target, name));
    },
  });
}

async function abortQuietly(tx: { abort(): void; done: Promise<unknown> }): Promise<void> {
  const done = tx.done;
  void done.catch(() => {});
  let abortFailure: { error: unknown } | null = null;
  try { tx.abort(); } catch (error) { abortFailure = { error }; }
  let aborted = false;
  try { await done; } catch { aborted = true; }
  // A request may have aborted the transaction before cleanup runs. Only its
  // rejected completion proves rollback; a completed commit is never an abort.
  if (abortFailure && !(aborted && abortFailure.error instanceof DOMException &&
      abortFailure.error.name === 'InvalidStateError')) throw abortFailure.error;
  if (!aborted) throw new Error('content-transaction-not-aborted');
}

async function assertContextBeforeCommit(
  tx: { abort(): void; done: Promise<unknown> },
  context: ContentLifecycleContext,
): Promise<void> {
  try {
    assertLifecycleContext(context);
    if (!isRestoreBypassActive()) {
      const ownerStore = (tx as unknown as {
        objectStore(name: typeof APP_META_STORE): {
          get(key: typeof CONTENT_OWNER_META_KEY): Promise<{ value?: unknown } | undefined>;
        };
      }).objectStore(APP_META_STORE);
      await assertLifecycleOwnerBinding(
        context.ownerId,
        () => ownerStore.get(CONTENT_OWNER_META_KEY),
      );
      assertLifecycleContext(context);
    }
  } catch (error) {
    await abortQuietly(tx);
    throw error;
  }
}

export async function getContentPartition(ownerId: string): Promise<ContentPartitionV1> {
  const db = await getDb();
  return (await db.get(CONTENT_PARTITIONS_STORE, ownerId)) ?? defaultPartition(ownerId);
}

export type ObsoleteReplayQuarantineCandidate = {
  observationKey: string;
  contentRevision: number;
  opId: string;
  frontier: number;
};

function digestMismatchObservations<T extends {
  observationKey: string;
  reason: string;
}>(observations: T[]): T[] {
  return observations.filter((observation) => observation.reason === 'digest-mismatch');
}

const UUID_V4_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function isUniqueRevisionObservationKey(
  observation: { observationKey: string; observedAt: string },
  contentRevision: number,
): boolean {
  const prefix = `${observation.observedAt}:revision-${contentRevision}`;
  if (!observation.observationKey.startsWith(`${prefix}:`)) return false;
  return UUID_V4_RE.test(observation.observationKey.slice(prefix.length + 1));
}

/**
 * Identify only the partition-only false-positive shape produced when an
 * already installed owner revision is delivered again after the same entity
 * has advanced. This is discovery, not recovery: the caller must still prove
 * the historical bytes against both authenticated server receipt and pull
 * paths before any quarantine metadata can change.
 */
export async function getObsoleteReplayQuarantineCandidate(
  context: ContentLifecycleContext,
): Promise<ObsoleteReplayQuarantineCandidate | null> {
  assertLifecycleContext(context);
  const db = await getDb();
  const tx = db.transaction(
    [
      CONTENT_PARTITIONS_STORE,
      CONTENT_OBSERVATIONS_STORE,
      CONTENT_ENTITIES_STORE,
      CONTENT_HOLDING_STORE,
    ],
    'readonly',
  );
  const [partition, observations, entities, holdingCount] = await Promise.all([
    tx.objectStore(CONTENT_PARTITIONS_STORE).get(context.ownerId),
    tx.objectStore(CONTENT_OBSERVATIONS_STORE).index('by-owner').getAll(context.ownerId),
    tx.objectStore(CONTENT_ENTITIES_STORE).index('by-owner').getAll(context.ownerId),
    tx.objectStore(CONTENT_HOLDING_STORE).index('by-owner').count(context.ownerId),
  ]);
  await tx.done;
  assertLifecycleContext(context);
  if (
    !partition ||
    partition.quarantineReason !== 'digest-mismatch' ||
    partition.heldReason !== null ||
    partition.frontier !== partition.lastObservedHighWater ||
    holdingCount !== 0 ||
    entities.some(
      (entity) =>
        entity.state === 'quarantined' ||
        entity.state === 'held' ||
        entity.quarantineReason !== null ||
        entity.heldReason !== null,
    )
  ) {
    return null;
  }
  const mismatchObservations = digestMismatchObservations(observations);
  if (mismatchObservations.length !== 1) return null;
  const observation = mismatchObservations[0];
  const classification = await classifyOrdinaryEnvelope(observation.raw, context.ownerId);
  assertLifecycleContext(context);
  if (classification.kind !== 'recognized') return null;
  const envelope = classification.envelope;
  if (
    !isUniqueRevisionObservationKey(observation, envelope.contentRevision) ||
    envelope.contentRevision > partition.frontier
  ) {
    return null;
  }
  const entity = entities.find(
    (candidate) =>
      candidate.entityType === envelope.entityType &&
      candidate.entityId === envelope.entityId,
  );
  if (
    !entity?.installedEnvelope ||
    entity.installedEnvelope.contentRevision <= envelope.contentRevision
  ) {
    return null;
  }
  return {
    observationKey: observation.observationKey,
    contentRevision: envelope.contentRevision,
    opId: envelope.opId,
    frontier: partition.frontier,
  };
}

/**
 * Clear only a proven obsolete-replay partition latch. Both independent
 * authenticated server lookup paths must reproduce the exact quarantined
 * envelope, and every local safety precondition is rechecked atomically.
 */
export async function recoverObsoleteReplayQuarantine(
  context: ContentLifecycleContext,
  candidate: ObsoleteReplayQuarantineCandidate,
  receipt: ContentOpReceiptV1,
  pulledRawEnvelope: unknown,
): Promise<boolean> {
  assertLifecycleWrite('content-lifecycle:recover-obsolete-replay-quarantine');
  assertLifecycleContext(context);
  await assertContentOpReceiptV1(receipt, context.ownerId);
  const db = await getDb();
  const observed = await db.get(CONTENT_OBSERVATIONS_STORE, [
    context.ownerId,
    candidate.observationKey,
  ]);
  if (!observed || observed.reason !== 'digest-mismatch') return false;
  const [observedClassification, pulledClassification] = await Promise.all([
    classifyOrdinaryEnvelope(observed.raw, context.ownerId),
    classifyOrdinaryEnvelope(pulledRawEnvelope, context.ownerId),
  ]);
  assertLifecycleContext(context);
  if (
    observedClassification.kind !== 'recognized' ||
    pulledClassification.kind !== 'recognized'
  ) {
    return false;
  }
  const observedEnvelope = observedClassification.envelope;
  if (
    observedEnvelope.contentRevision !== candidate.contentRevision ||
    observedEnvelope.opId !== candidate.opId ||
    receipt.envelope.contentRevision !== candidate.contentRevision ||
    receipt.envelope.opId !== candidate.opId ||
    pulledClassification.envelope.contentRevision !== candidate.contentRevision ||
    !envelopeBytesEqual(observedEnvelope, receipt.envelope) ||
    !envelopeBytesEqual(observedEnvelope, pulledClassification.envelope)
  ) {
    return false;
  }

  const tx = db.transaction(
    [
      CONTENT_PARTITIONS_STORE,
      CONTENT_OBSERVATIONS_STORE,
      CONTENT_ENTITIES_STORE,
      CONTENT_HOLDING_STORE,
      APP_META_STORE,
    ],
    'readwrite',
  );
  const partitions = tx.objectStore(CONTENT_PARTITIONS_STORE);
  const [partition, currentObservation, observations, entities, holdingCount] =
    await Promise.all([
      partitions.get(context.ownerId),
      tx.objectStore(CONTENT_OBSERVATIONS_STORE).get([
        context.ownerId,
        candidate.observationKey,
      ]),
      tx.objectStore(CONTENT_OBSERVATIONS_STORE).index('by-owner').getAll(context.ownerId),
      tx.objectStore(CONTENT_ENTITIES_STORE).index('by-owner').getAll(context.ownerId),
      tx.objectStore(CONTENT_HOLDING_STORE).index('by-owner').count(context.ownerId),
    ]);
  const mismatchObservations = digestMismatchObservations(observations);
  const entity = entities.find(
    (item) =>
      item.entityType === observedEnvelope.entityType &&
      item.entityId === observedEnvelope.entityId,
  );
  const safe = Boolean(
    partition &&
      partition.quarantineReason === 'digest-mismatch' &&
      partition.heldReason === null &&
      partition.frontier === candidate.frontier &&
      partition.frontier === partition.lastObservedHighWater &&
      holdingCount === 0 &&
      currentObservation?.reason === 'digest-mismatch' &&
      mismatchObservations.length === 1 &&
      mismatchObservations[0]?.observationKey === candidate.observationKey &&
      isUniqueRevisionObservationKey(currentObservation, candidate.contentRevision) &&
      bytesEqual(cj1Bytes(currentObservation.raw), cj1Bytes(observedEnvelope)) &&
      entities.every(
        (item) =>
          item.state !== 'quarantined' &&
          item.state !== 'held' &&
          item.quarantineReason === null &&
          item.heldReason === null,
      ) &&
      entity?.installedEnvelope &&
      entity.installedEnvelope.contentRevision > candidate.contentRevision,
  );
  if (!safe || !partition) {
    await abortQuietly(tx);
    return false;
  }
  void partitions.put({ ...partition, quarantineReason: null });
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId });
  return true;
}

export async function hasContentLifecyclePartition(ownerId: string): Promise<boolean> {
  const db = await getDb();
  return (await db.get(CONTENT_PARTITIONS_STORE, ownerId))?.legacyBootstrapComplete === true;
}

export async function hasContentLifecycleEntityBootstrap(
  ownerId: string,
  entityType: ContentEntityType,
): Promise<boolean> {
  const db = await getDb();
  const partition = await db.get(CONTENT_PARTITIONS_STORE, ownerId);
  return partition?.legacyBootstrapComplete === true ||
    partition?.legacyBootstrapEntityTypes?.includes(entityType) === true;
}

export type LegacyContentBootstrapSnapshot = {
  prayers: Prayer[];
  bookmarks: BibleBookmark[];
  highlights: BibleHighlight[];
  entityTypes: ContentEntityType[];
};

let legacyBootstrapReadProbe: ((entityType: ContentEntityType) => void) | null = null;

/** Node-probe-only witness for physical legacy-store reads. */
export function __setLegacyBootstrapReadProbe(
  probe: ((entityType: ContentEntityType) => void) | null,
): void {
  if (!isRestoreBypassActive()) throw new Error('content-bootstrap-probe-unavailable');
  legacyBootstrapReadProbe = probe;
}

/** Capture verified-owner legacy bytes before the first successful S1 pull. */
export async function captureLegacyContentBootstrap(
  context: ContentLifecycleContext,
  enabledEntityTypes: ReadonlySet<ContentEntityType> = ALL_CONTENT_ENTITY_TYPE_SET,
): Promise<LegacyContentBootstrapSnapshot | null> {
  assertLifecycleContext(context);
  const db = await getDb();
  const partition = await db.get(CONTENT_PARTITIONS_STORE, context.ownerId);
  if (partition?.legacyBootstrapComplete === true) return null;
  const completed = new Set(partition?.legacyBootstrapEntityTypes ?? []);
  const entityTypes = ALL_CONTENT_ENTITY_TYPES.filter(
    (entityType) => enabledEntityTypes.has(entityType) && !completed.has(entityType),
  );
  if (entityTypes.length === 0) return null;
  for (const entityType of entityTypes) legacyBootstrapReadProbe?.(entityType);
  const [prayers, bookmarks, highlights] = await Promise.all([
    entityTypes.includes('prayer') ? db.getAll(PRAYERS_STORE) : Promise.resolve([] as Prayer[]),
    entityTypes.includes('bookmark')
      ? db.getAll(BIBLE_BOOKMARKS_STORE)
      : Promise.resolve([] as BibleBookmark[]),
    entityTypes.includes('highlight')
      ? db.getAll(BIBLE_HIGHLIGHTS_STORE)
      : Promise.resolve([] as BibleHighlight[]),
  ]);
  assertLifecycleContext(context);
  return { prayers, bookmarks, highlights, entityTypes };
}

export async function getLocalContentEntity(
  ownerId: string,
  entityType: ContentEntityType,
  entityId: string,
): Promise<LocalContentEntityV1 | null> {
  const db = await getDb();
  return (await db.get(CONTENT_ENTITIES_STORE, entityKey(ownerId, entityType, entityId))) ?? null;
}

export async function getOwnerContentEntities(ownerId: string): Promise<LocalContentEntityV1[]> {
  const db = await getDb();
  return db.getAllFromIndex(CONTENT_ENTITIES_STORE, 'by-owner', ownerId);
}

/** Retired compatibility seam: new audio binding is authorized only in the
 * transaction that commits the exact authored destination and consumes intent. */
export async function attachInlineAudioToPendingOperation(
  context: ContentLifecycleContext,
  _queueItemId: string,
  _source: 'prayer' | 'bookmark',
  _committedText: string,
  _entityType: 'prayer' | 'bookmark',
  _entityId: string,
): Promise<AudioQueueRow | null> {
  assertLifecycleWrite('content-lifecycle:attach-inline-audio');
  assertLifecycleContext(context);
  void [_queueItemId, _source, _committedText, _entityType, _entityId];
  return null;
}

export async function getOwnerConflicts(ownerId: string): Promise<ConflictRecordV1[]> {
  const db = await getDb();
  return db.getAllFromIndex(CONTENT_CONFLICTS_STORE, 'by-owner', ownerId);
}

export async function getConflictRecord(
  ownerId: string,
  entityType: ContentEntityType,
  entityId: string,
): Promise<ConflictRecordV1 | null> {
  const db = await getDb();
  return (await db.get(CONTENT_CONFLICTS_STORE, entityKey(ownerId, entityType, entityId))) ?? null;
}

function buildLiveMutation(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  document: CompleteLiveDocument,
  opKind: 'create' | 'update' | 'restore',
  mutationSeq: string,
  expectedBaseRevision: number | null,
  opId: string,
): ContentMutationV1 {
  return {
    schema: 'adia-content-mutation-v1',
    protocolVersion: 1,
    ownerId: context.ownerId,
    entityType,
    entityId: document.id,
    opId,
    opKind,
    mutationSeq,
    expectedBaseRevision,
    payload: { kind: 'live', document },
  };
}

async function freezeTuple(
  context: ContentLifecycleContext,
  mutation: ContentMutationV1,
): Promise<DurablePendingContentTupleV1> {
  assertContentMutationV1(mutation);
  const completePendingBytes = cj1Bytes(mutation);
  const contentDigest = await contentMutationDigest(mutation);
  return {
    ownerId: context.ownerId,
    authEpoch: context.authEpoch,
    generation: context.generation,
    entityType: mutation.entityType,
    entityId: mutation.entityId,
    opKind: mutation.opKind,
    mutationSeq: mutation.mutationSeq,
    opId: mutation.opId,
    completePendingBytes,
    contentDigest,
    expectedBaseRevision: mutation.expectedBaseRevision,
  };
}

function tupleMatchesEnvelope(
  tuple: DurablePendingContentTupleV1 | null | undefined,
  envelope: OrdinaryContentEnvelopeV1,
): boolean {
  return Boolean(
    tuple &&
      tuple.ownerId === envelope.ownerId &&
      tuple.entityType === envelope.entityType &&
      tuple.entityId === envelope.entityId &&
      tuple.opId === envelope.opId &&
      tuple.opKind === envelope.opKind &&
      tuple.expectedBaseRevision === envelope.expectedBaseRevision &&
      tuple.mutationSeq === envelope.mutationSeq &&
      tuple.contentDigest === envelope.contentDigest &&
      bytesEqual(tuple.completePendingBytes, cj1Bytes(projectMutation(envelope))),
  );
}

function rebaseLocalSuccessorMutation(
  context: ContentLifecycleContext,
  successor: ContentMutationV1,
  predecessor: OrdinaryContentEnvelopeV1,
): ContentMutationV1 {
  if (
    successor.ownerId !== predecessor.ownerId ||
    successor.entityType !== predecessor.entityType ||
    successor.entityId !== predecessor.entityId
  ) {
    throw new Error('content-successor-predecessor-mismatch');
  }
  const opId = mintUuidV4();
  if (successor.payload.kind === 'live') {
    return buildLiveMutation(
      context,
      successor.entityType,
      successor.payload.document,
      predecessor.payload.kind === 'tombstone' ? 'restore' : 'update',
      successor.mutationSeq,
      predecessor.contentRevision,
      opId,
    );
  }
  if (predecessor.payload.kind !== 'live') {
    throw new Error('content-delete-successor-requires-live-predecessor');
  }
  const deleteSuccessor = successor as TombstoneMutationV1;
  return {
    schema: 'adia-content-mutation-v1',
    protocolVersion: 1,
    ownerId: deleteSuccessor.ownerId,
    entityType: deleteSuccessor.entityType,
    entityId: deleteSuccessor.entityId,
    opId,
    opKind: 'delete',
    mutationSeq: deleteSuccessor.mutationSeq,
    expectedBaseRevision: predecessor.contentRevision,
    payload: {
      kind: 'tombstone-intent',
      predecessorLiveDigest: predecessor.contentDigest,
      predecessorLiveSnapshot: predecessor.payload.document,
      tombstonedAt: deleteSuccessor.payload.tombstonedAt,
    },
  };
}

type AuthoredRecoveredAudioClaim =
  | RecoveredAudioCommitClaim
  | {
      queueItemId: string;
      recoveryKind: 'new-prayer';
      expectedTranscribedText: string;
    };

async function commitAuthoredMutation(
  context: ContentLifecycleContext,
  snapshot: LocalContentEntityV1 | undefined,
  mutation: ContentMutationV1,
  tuple: DurablePendingContentTupleV1,
  visibleDocument: CompleteLiveDocument | null,
  deleteConflict: boolean,
  prayerProjection?: { expected?: Prayer; next: Prayer },
  recoveredAudio: readonly AuthoredRecoveredAudioClaim[] = [],
  prayerAttempt?: PrayerAttemptV1,
): Promise<boolean> {
  assertLifecycleContext(context);
  const db = await getDb();
  // KR8 (db.ts "Verified capture copies"): the claimed recordings, and those
  // rebound from a coalesced pending operation, are written below with a
  // verified copy of any Blob they still hold, proved here, before the
  // transaction; inside it a copy is used only when the guard accepts it for
  // the row read there, otherwise the write is exactly as before.
  const coalescedAudioQueueIds = snapshot?.pending
    ? ((await db.get(CONTENT_PENDING_STORE, [context.ownerId, snapshot.pending.opId])) as
        StoredPendingContentOperationV1 | undefined)?.audioQueueIds ?? []
    : [];
  const audioCopies = await verifiedCaptureCopiesBeforeWrite(
    [...recoveredAudio.map((claim) => claim.queueItemId), ...coalescedAudioQueueIds],
    context.ownerId,
  );
  const stores = mutation.entityType === 'prayer'
    ? [
        CONTENT_ENTITIES_STORE,
        CONTENT_PENDING_STORE,
        CONTENT_CONFLICTS_STORE,
        APP_META_STORE,
        KEEPSAKE_FENCES_STORE,
        KEEPSAKE_META_STORE,
        PRAYERS_STORE,
        AUDIO_QUEUE_STORE,
      ] as const
    : [
        CONTENT_ENTITIES_STORE,
        CONTENT_PENDING_STORE,
        CONTENT_CONFLICTS_STORE,
        APP_META_STORE,
        projectionStore(mutation.entityType),
        AUDIO_QUEUE_STORE,
      ] as const;
  // Every write queued in this transaction, here or in the projection and fence
  // helpers, is observed; an abort still fails the author through `tx.done`.
  const tx = observeQueuedWrites(db.transaction(stores, 'readwrite'));
  const key = entityKey(context.ownerId, mutation.entityType, mutation.entityId);
  // Observe abort immediately even if an awaited request rejects before the
  // final completion await. The author still receives the abort as failure.
  void tx.done.catch(() => {});
  const [
    current,
    conflict,
    currentPrayerProjection,
    currentPendingOperation,
    currentAudioRows,
  ] = await Promise.all([
    tx.objectStore(CONTENT_ENTITIES_STORE).get(key),
    tx.objectStore(CONTENT_CONFLICTS_STORE).get(key),
    mutation.entityType === 'prayer'
      ? tx.objectStore(PRAYERS_STORE).get(mutation.entityId)
      : Promise.resolve(undefined),
    snapshot?.pending
      ? tx.objectStore(CONTENT_PENDING_STORE).get([context.ownerId, snapshot.pending.opId])
      : Promise.resolve(undefined),
    recoveredAudio.length > 0
      ? Promise.all(
          recoveredAudio.map((claim) =>
            tx.objectStore(AUDIO_QUEUE_STORE).get(claim.queueItemId),
          ),
        )
      : Promise.resolve([]),
  ]);
  const projectionMatches = (() => {
    if (!prayerProjection?.expected) return true;
    const currentPrayer = currentPrayerProjection as Prayer | undefined;
    if (!currentPrayer) return false;
    const expected = prayerProjection.expected;
    return (
      JSON.stringify(completePrayerDocument(currentPrayer)) === JSON.stringify(completePrayerDocument(expected)) &&
      currentPrayer.updatedAt === expected.updatedAt &&
      JSON.stringify(currentPrayer.pendingEnrichment ?? null) ===
        JSON.stringify(expected.pendingEnrichment ?? null) &&
      currentPrayer.pendingTranscriptionId === expected.pendingTranscriptionId &&
      currentPrayer.transcriptionFailed === expected.transcriptionFailed
    );
  })();
  const recoveredAudioMatches = (() => {
    if (recoveredAudio.length === 0) return true;
    if (new Set(recoveredAudio.map((claim) => claim.queueItemId)).size !== recoveredAudio.length) {
      return false;
    }
    const projection = currentPrayerProjection as Prayer | undefined;
    const nextProjection = prayerProjection?.next;
    return recoveredAudio.every((claim, index) => {
      const row = currentAudioRows[index] as AudioQueueRow | undefined;
      const recovered = row?.transcribedText?.trim() ?? '';
      if (
        !row ||
        row.id !== claim.queueItemId ||
        row.ownerId !== context.ownerId ||
        row.audioProtocolVersion !== 1 ||
        (row.captureMode !== 'enhanced' && row.captureMode !== 'browser') ||
        !row.destinationId ||
        row.localDisposition !== 'unresolved' ||
        (row.destinationState !== undefined && row.destinationState !== 'unresolved') ||
        row.destinationOpId != null ||
        (!enhancedVoiceAllowed() && audioQueueRowCarriesBytes(row))
      ) {
        return false;
      }
      if (
        claim.recoveryKind === 'inline-prayer' ||
        claim.recoveryKind === 'inline-bookmark'
      ) {
        if (
          !recovered ||
          recovered !== claim.expectedTranscribedText ||
          row.prayerId !== null ||
          // DS (duplicate-save follow-through, 2026-10-20): an inline-prayer
          // claim also takes the resume card's recording whose words Continue
          // moved into the Home draft (not awaiting an inline save); the
          // prayer-source, new-prayer, anchor and containment checks below
          // apply to it unchanged (audio-queue.ts heldResumeCardRecording).
          (row.awaitingInlineSave !== true &&
            !(claim.recoveryKind === 'inline-prayer' &&
              row.recoveryKind === 'new-prayer' &&
              row.inlineSaveSource === 'prayer')) ||
          row.localDisposition !== 'unresolved'
        ) return false;
        if (claim.recoveryKind === 'inline-prayer') {
          const prayer = visibleDocument as CompletePrayerDocument | null;
          const sourceAnchor = parseScriptureAnchor(row.scriptureAnchor);
          const targetAnchor = parseScriptureAnchor(prayer?.scriptureAnchor);
          const anchorMatches = row.scriptureAnchor == null && prayer?.scriptureAnchor == null
            || Boolean(sourceAnchor && targetAnchor && scriptureAnchorsEqual(sourceAnchor, targetAnchor));
          return anchorMatches && mutation.entityType === 'prayer' &&
            row.recoveryKind === 'new-prayer' &&
            row.inlineSaveSource === 'prayer' &&
            visibleDocument?.id === mutation.entityId &&
            containsRecoveredVoiceText(
              (visibleDocument as CompletePrayerDocument).rawText,
              recovered,
            );
        }
        const bookmark = visibleDocument as CompleteBookmarkDocument | null;
        const recoveryAnchor = parseScriptureAnchor(row.scriptureAnchor);
        const bookmarkAnchor = parseScriptureAnchor(bookmark?.anchor);
        return mutation.entityType === 'bookmark' &&
          row.recoveryKind === 'bookmark' &&
          row.inlineSaveSource === 'bookmark' &&
          bookmark?.id === mutation.entityId &&
          containsRecoveredVoiceText(bookmark.note, recovered) &&
          Boolean(
            recoveryAnchor &&
            bookmarkAnchor &&
            scriptureAnchorsEqual(recoveryAnchor, bookmarkAnchor),
          );
      }
      if (
        mutation.entityType !== 'prayer' ||
        !projection ||
        !nextProjection ||
        projection.id !== mutation.entityId ||
        row.prayerId !== mutation.entityId
      ) return false;
      // Destination proof, by capture kind. An update or testimony capture is
      // bound to its Prayer from the first durable byte (useSpeechToText mints
      // destinationId = recoveryDestination.prayerId and db.commitCaptureRow
      // freezes it), so its register-pinned destinationId must name this
      // entity, the same rule the server bind applies to those two kinds
      // (044 bind_audio_destination_v1). A new-prayer capture carries an
      // opaque per-capture destinationId; its proof is the atomic link that
      // db.savePendingPrayerAndLinkAudio wrote (row.prayerId = prayer.id and
      // prayer.pendingTranscriptionId = row.id in one owner-checked
      // transaction), re-proved here against the row reread inside THIS
      // transaction and the current projection. No identity is assigned
      // anywhere; the link is the relationship.
      const destinationIsPrayerBound =
        claim.recoveryKind === 'progress-update' || claim.recoveryKind === 'testimony';
      if (destinationIsPrayerBound) {
        if (row.destinationId !== mutation.entityId) return false;
      } else if (
        (row.recoveryKind != null && row.recoveryKind !== 'new-prayer') ||
        projection.pendingTranscriptionId !== claim.queueItemId
      ) return false;
      if (claim.recoveryKind === 'typed-replacement') {
        return (
          row.recoveryKind === 'new-prayer' &&
          projection.pendingTranscriptionId === claim.queueItemId &&
          projection.transcriptionFailed === true &&
          !nextProjection.pendingTranscriptionId &&
          nextProjection.rawText.trim() !== ''
        );
      }
      if (!recovered) return false;
      if (claim.recoveryKind === 'new-prayer') {
        return (
          (row.recoveryKind == null || row.recoveryKind === 'new-prayer') &&
          recovered === claim.expectedTranscribedText &&
          projection.pendingTranscriptionId === claim.queueItemId &&
          containsRecoveredVoiceText(nextProjection.rawText, recovered)
        );
      }
      if (row.recoveryKind !== claim.recoveryKind || recovered !== claim.expectedTranscribedText) return false;
      if (claim.recoveryKind === 'testimony') {
        return containsRecoveredVoiceText(nextProjection.testimony ?? '', recovered);
      }
      const entries = (nextProjection.progressHistory ?? []).filter(
        (entry) => entry.id === claim.progressEntryId,
      );
      return (
        entries.length === 1 &&
        containsRecoveredVoiceText(entries[0].note, recovered)
      );
    });
  })();
  if (
    (prayerAttempt && (await tx.objectStore(APP_META_STORE).get(prayerAttemptKey(context.ownerId, prayerAttempt.commandId)))?.value !== JSON.stringify(prayerAttempt)) ||
    (prayerAttempt?.intent.kind === 'create' && (currentPrayerProjection !== undefined || await tx.objectStore(KEEPSAKE_FENCES_STORE).get(mutation.entityId) !== undefined)) ||
    !entitySnapshotEqual(current, snapshot) ||
    !projectionMatches ||
    !recoveredAudioMatches ||
    (deleteConflict ? !conflict : Boolean(conflict))
  ) {
    await abortQuietly(tx);
    return false;
  }
  // O3 (testimony drafts), read phase: only a testimony attempt reads its one
  // deterministic draft key, BEFORE any write is queued, so an exception aborts a
  // transaction with nothing pending and nothing can commit without the owner
  // check below. The parser is total; a row it rejects is never touched.
  let draftAction: { key: string; value: string | null } | null = null;
  if (prayerAttempt?.intent.kind === 'testimony') {
    try {
      const draftKey = testimonyDraftKey(context.ownerId, prayerAttempt.prayerId);
      const draft = parseTestimonyDraftRow(await tx.objectStore(APP_META_STORE).get(draftKey));
      if (draft && draft.link?.commandId === prayerAttempt.commandId) {
        draftAction = testimonyDraftLinkedTo(draft, prayerAttempt) && testimonyDraftSameVersion(draft)
          ? { key: draftKey, value: null }
          : { key: draftKey, value: serializeTestimonyDraft({ ...draft, link: null }) };
      }
    } catch (error) {
      await abortQuietly(tx);
      throw error;
    }
  }
  // Capture-row guard (account-change recording plan rev 4.1, 4.4), read
  // phase: each recovered-audio binding is built and checked BEFORE any write
  // is queued, so a refusal aborts a transaction with nothing pending. Binding
  // changes the destination fields, never a capture's frozen identity or payload.
  // Any exception in this added work is a refusal that aborts the transaction.
  let boundAudioRows: AudioQueueRow[] = [];
  let bindingRefusal: Error | null = null;
  try {
    boundAudioRows = recoveredAudio.map((claim, index): AudioQueueRow => {
      const row = currentAudioRows[index] as AudioQueueRow;
      const destinationSubId = claim.recoveryKind === 'progress-update'
        ? claim.progressEntryId
        : claim.recoveryKind === 'testimony'
          ? 'testimony'
          : undefined;
      return keepVerifiedCaptureCopy(row, {
        ...row,
        prayerId: mutation.entityType === 'prayer' ? mutation.entityId : row.prayerId,
        localDisposition: 'destination-commit-pending',
        destinationEntityType: mutation.entityType as 'prayer' | 'bookmark',
        destinationEntityId: mutation.entityId,
        destinationSubId,
        destinationOpId: tuple.opId,
      }, audioCopies.get(claim.queueItemId));
    });
    for (let index = 0; index < boundAudioRows.length && !bindingRefusal; index += 1) {
      bindingRefusal = captureRowTransitionRefusal(
        currentAudioRows[index] as AudioQueueRow,
        boundAudioRows[index],
      );
    }
  } catch (error) {
    bindingRefusal = error instanceof Error ? error : new Error('capture-row-guard-failed');
  }
  if (bindingRefusal) await abortForCaptureRefusal(tx, bindingRefusal);
  const next: LocalContentEntityV1 = {
    ownerId: context.ownerId,
    entityType: mutation.entityType,
    entityId: mutation.entityId,
    state: mutation.opKind === 'delete' ? 'tombstoned' : 'dirty',
    installedEnvelope: current?.installedEnvelope ?? null,
    visibleDocument,
    pending: tuple,
    pendingDeleteAt: null,
    mutationSeq: tuple.mutationSeq,
    localBaseRevision: current?.localBaseRevision ?? null,
    heldReason: null,
    quarantineReason: null,
    recovery: 'noop',
  };
  if (mutation.payload.kind === 'live') {
    await putLiveProjection(
      tx as unknown as Parameters<typeof putLiveProjection>[0],
      mutation.entityType,
      mutation.payload.document,
      prayerProjection?.next,
    );
  } else {
    await putTombstoneProjection(
      tx as unknown as Parameters<typeof putTombstoneProjection>[0],
      mutation.entityType,
      mutation.entityId,
      mutation.payload.tombstonedAt,
    );
  }
  const fenceMarks = mutation.entityType === 'prayer'
    ? await bumpLifecyclePrayerFenceInTx(
        tx as unknown as Parameters<typeof bumpLifecyclePrayerFenceInTx>[0],
        mutation.entityId,
        context.ownerId,
        'local',
      )
    : null;
  const coalescedPending =
    current?.pending &&
    currentPendingOperation?.dispatchState === 'pending' &&
    pendingIdentityEqual(current.pending, currentPendingOperation.tuple)
      ? currentPendingOperation
      : null;
  const stored: StoredPendingContentOperationV1 = {
    ownerId: context.ownerId,
    opId: tuple.opId,
    entityType: tuple.entityType,
    entityId: tuple.entityId,
    tuple,
    mutation,
    prayerFenceMutationSeq: fenceMarks?.seq ?? null,
    audioQueueIds: (() => {
      const ids = [
        ...(coalescedPending?.audioQueueIds ?? []),
        ...recoveredAudio.map((claim) => claim.queueItemId),
      ];
      return ids.length > 0 ? [...new Set(ids)] : undefined;
    })(),
    dispatchState: 'pending',
  };
  void tx.objectStore(CONTENT_PENDING_STORE).put(stored);
  if (coalescedPending && coalescedPending.opId !== stored.opId) {
    void tx.objectStore(CONTENT_PENDING_STORE).delete([
      context.ownerId,
      coalescedPending.opId,
    ]);
  }
  void tx.objectStore(CONTENT_ENTITIES_STORE).put(next);
  for (const queueItemId of coalescedPending?.audioQueueIds ?? []) {
    if (recoveredAudio.some((claim) => claim.queueItemId === queueItemId)) continue;
    const prior = await tx.objectStore(AUDIO_QUEUE_STORE).get(queueItemId);
    if (
      prior?.ownerId === context.ownerId &&
      prior.destinationOpId === coalescedPending?.opId
    ) {
      // Read after other writes were queued: a refusal, or any exception in
      // this added work, aborts all of them before it propagates.
      let rebound: AudioQueueRow | null = null;
      let refusal: Error | null = null;
      try {
        const candidate = keepVerifiedCaptureCopy(prior, { ...prior, destinationOpId: tuple.opId }, audioCopies.get(queueItemId));
        rebound = candidate;
        refusal = captureRowTransitionRefusal(prior, candidate);
      } catch (error) {
        refusal = error instanceof Error ? error : new Error('capture-row-guard-failed');
      }
      if (refusal || !rebound) await abortForCaptureRefusal(tx, refusal ?? new Error('capture-row-guard-failed'));
      void tx.objectStore(AUDIO_QUEUE_STORE).put(rebound as AudioQueueRow);
    }
  }
  for (const bound of boundAudioRows) {
    // The local author transaction records intent; it does NOT delete audio.
    // Before transport, sync binds this queue id to the exact op id. After the
    // server content receipt exists, the registry deliberately marks the
    // destination committed and issues the only cleanup authority.
    void tx.objectStore(AUDIO_QUEUE_STORE).put(bound);
  }
  if (deleteConflict) void tx.objectStore(CONTENT_CONFLICTS_STORE).delete(key);
  if (prayerAttempt) void tx.objectStore(APP_META_STORE).delete(prayerAttemptKey(context.ownerId, prayerAttempt.commandId));
  // O3, write phase: a draft linked to this command is consumed when it names this
  // exact revision and text version; otherwise it is unlinked and its text kept.
  if (draftAction) {
    if (draftAction.value === null) void tx.objectStore(APP_META_STORE).delete(draftAction.key);
    else void tx.objectStore(APP_META_STORE).put({ key: draftAction.key, value: draftAction.value });
  }
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId, entityType: mutation.entityType, entityId: mutation.entityId });
  // Durable success is tx.done. Observers cannot reinterpret a committed write.
  if (fenceMarks) { try { finalizeLifecyclePrayerFence(mutation.entityId, fenceMarks); } catch { /* next hydrate repairs the mirror */ } }
  return true;
}

/** Register once. Retry only authorPrayerAttempt; it never recreates this row. */
export async function registerPrayerAttempt(
  context: ContentLifecycleContext,
  prayerId: string,
  intent: PrayerIntent,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<PrayerAttemptV1> {
  assertLifecycleWrite('content-lifecycle:prayer-attempt-register');
  assertLifecycleContext(context);
  const attempt: PrayerAttemptV1 = { schema: 'adia-prayer-attempt-v1', ownerId: context.ownerId,
    commandId: mintUuidV4(), revision: 1, prayerId, authoredAt: new Date().toISOString(),
    intent: structuredClone(intent), recoveredAudio: structuredClone([...recoveredAudio]) };
  if (!parsePrayerAttemptRow({ key: prayerAttemptKey(context.ownerId, attempt.commandId), value: JSON.stringify(attempt) })) throw new Error('prayer-attempt-malformed');
  const db = await getDb();
  const tx = observeQueuedWrites(db.transaction(APP_META_STORE, 'readwrite'));
  void tx.objectStore(APP_META_STORE).add({ key: prayerAttemptKey(context.ownerId, attempt.commandId), value: JSON.stringify(attempt) });
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyPrayerAttemptsChanged();
  return attempt;
}

export async function listPrayerAttempts(ownerId: string): Promise<PrayerAttemptV1[]> {
  const db = await getDb();
  return (await db.getAll(APP_META_STORE)).map(parsePrayerAttemptRow)
    .filter((attempt): attempt is PrayerAttemptV1 => attempt !== null && attempt.ownerId === ownerId);
}

/** Explicit review/revision or abandonment; late callbacks cannot consume newer drafts. */
export async function revisePrayerAttempt(
  context: ContentLifecycleContext,
  previous: PrayerAttemptV1,
  intent: PrayerIntent | null,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = previous.recoveredAudio,
): Promise<PrayerAttemptV1 | null> {
  assertLifecycleWrite('content-lifecycle:prayer-attempt-revise');
  assertLifecycleContext(context);
  if (previous.ownerId !== context.ownerId) throw new Error('prayer-attempt-owner-mismatch');
  const key = prayerAttemptKey(context.ownerId, previous.commandId);
  const db = await getDb();
  const tx = observeQueuedWrites(db.transaction(APP_META_STORE, 'readwrite'));
  void tx.done.catch(() => {});
  const store = tx.objectStore(APP_META_STORE);
  let next: PrayerAttemptV1 | null;
  try {
    const current = await store.get(key);
    if (current?.value !== JSON.stringify(previous)) throw new Error('prayer-attempt-revision-stale');
    next = intent ? { ...previous, revision: previous.revision + 1, intent: structuredClone(intent), recoveredAudio: structuredClone([...recoveredAudio]) } : null;
    if (next && !parsePrayerAttemptRow({ key, value: JSON.stringify(next) })) throw new Error('prayer-attempt-malformed');
    // O6 / O7 read phase, before any write. Only a draft LINKED to the exact
    // reviewed revision follows it; malformed and foreign rows stay untouched.
    let draftAction: { key: string; value: string | null } | null = null;
    if (previous.intent.kind === 'testimony') {
      const draftKey = testimonyDraftKey(context.ownerId, previous.prayerId);
      const draft = parseTestimonyDraftRow(await store.get(draftKey));
      if (draft?.link && testimonyDraftLinkedTo(draft, previous)) {
        draftAction = next
          ? { key: draftKey, value: serializeTestimonyDraft({ ...draft, link: { ...draft.link, attemptRevision: next.revision } }) }
          : draft.link.purpose === 'save' && testimonyDraftSameVersion(draft)
            ? { key: draftKey, value: null }
            : { key: draftKey, value: serializeTestimonyDraft({ ...draft, link: null }) };
      }
    }
    if (next) void store.put({ key, value: JSON.stringify(next) }); else void store.delete(key);
    if (draftAction) {
      if (draftAction.value === null) void store.delete(draftAction.key);
      else void store.put({ key: draftAction.key, value: draftAction.value });
    }
    await assertContextBeforeCommit(tx, context);
    await tx.done;
  } catch (error) {
    // Also rolls back an earlier queued write when a later write throws
    // synchronously. Request observers alone cannot provide that rollback.
    await abortQuietly(tx);
    throw error;
  }
  // Completion is the success boundary. Post-commit observers cannot enter
  // the rollback path or turn a committed transaction into a rollback claim.
  notifyPrayerAttemptsChanged();
  return next;
}

// ---- Testimony drafts (plan rev 4, sections 3.1 to 3.4) ---------------------
// Every operation below is exactly one `app-meta` transaction with the lifecycle
// write guard, the context check, and the in-transaction owner binding check
// before commit (rule #90). Nothing is caught and swallowed inside a
// transaction: a refusal aborts it and is rethrown; nothing is written.

type TestimonyIntent = Extract<PrayerIntent, { kind: 'testimony' }>;
type AppMetaStoreHandle = {
  get(key: string): Promise<{ key: string; value?: unknown } | undefined>;
  getAll(query: IDBKeyRange): Promise<Array<{ key: string; value?: unknown }>>;
  add(value: { key: string; value: string }): Promise<unknown>;
  put(value: { key: string; value: string }): Promise<unknown>;
  delete(key: string): Promise<unknown>;
};

/** Identity of a draft as the caller last read it (O1, O2, O4). */
export type TestimonyDraftExpectation = { draftId: string; textRevision: number } | null;

function refuse(code: string): never {
  throw new Error(code);
}

function prefixRange(prefix: string): IDBKeyRange {
  return IDBKeyRange.bound(prefix, `${prefix}￿`);
}

async function runTestimonyDraftTransaction<T>(
  context: ContentLifecycleContext,
  op: string,
  work: (store: AppMetaStoreHandle) => Promise<T>,
): Promise<T> {
  assertLifecycleWrite(op);
  assertLifecycleContext(context);
  const db = await getDb();
  const tx = observeQueuedWrites(db.transaction(APP_META_STORE, 'readwrite'));
  void tx.done.catch(() => {});
  let result: T;
  try {
    result = await work(tx.objectStore(APP_META_STORE) as unknown as AppMetaStoreHandle);
  } catch (error) {
    await abortQuietly(tx);
    throw error;
  }
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyPrayerAttemptsChanged();
  return result;
}

/** The stored draft at the key: absent, a valid draft, or a foreign row. */
async function readDraftSlotInTx(store: AppMetaStoreHandle, ownerId: string, prayerId: string) {
  const row = await store.get(testimonyDraftKey(ownerId, prayerId));
  if (row === undefined) return { raw: null as string | null, draft: null as TestimonyDraftV1 | null, foreign: false };
  const draft = parseTestimonyDraftRow(row);
  return { raw: typeof row.value === 'string' ? row.value : null, draft, foreign: draft === null };
}

async function attemptsForPrayerInTx(store: AppMetaStoreHandle, ownerId: string, prayerId: string) {
  const rows = await store.getAll(prefixRange(`${PRAYER_ATTEMPT_PREFIX_FOR_DRAFTS}${ownerId}:`));
  return rows.map(parsePrayerAttemptRow)
    .filter((attempt): attempt is PrayerAttemptV1 => attempt !== null && attempt.ownerId === ownerId && attempt.prayerId === prayerId);
}

async function assertStoredAttemptExact(store: AppMetaStoreHandle, context: ContentLifecycleContext, attempt: PrayerAttemptV1) {
  if (attempt.ownerId !== context.ownerId) refuse('prayer-attempt-owner-mismatch');
  if (attempt.intent.kind !== 'testimony') refuse('testimony-intent-required');
  const row = await store.get(prayerAttemptKey(context.ownerId, attempt.commandId));
  if (row === undefined) refuse('prayer-attempt-not-pending');
  if (row.value !== JSON.stringify(attempt)) refuse('prayer-attempt-revision-stale');
}

function draftExpectationMatches(draft: TestimonyDraftV1 | null, expected: TestimonyDraftExpectation): boolean {
  if (draft === null || expected === null) return draft === null && expected === null;
  return draft.draftId === expected.draftId && draft.textRevision === expected.textRevision;
}

function newDraft(context: ContentLifecycleContext, prayerId: string, text: string,
  baseTestimony: string | null, baseStatus: Prayer['status']): TestimonyDraftV1 {
  return { schema: TESTIMONY_DRAFT_SCHEMA, ownerId: context.ownerId, prayerId, draftId: mintUuidV4(),
    textRevision: 1, text, baseTestimony, baseStatus, link: null };
}

function withText(draft: TestimonyDraftV1, text: string): TestimonyDraftV1 {
  return draft.text === text ? draft : { ...draft, text, textRevision: draft.textRevision + 1 };
}

function assertDraftText(text: string): void {
  if (typeof text !== 'string' || text.length > TESTIMONY_DRAFT_MAX_TEXT) refuse('testimony-draft-malformed');
}

function putDraft(store: AppMetaStoreHandle, draft: TestimonyDraftV1): TestimonyDraftSlot {
  const raw = serializeTestimonyDraft(draft);
  const key = testimonyDraftKey(draft.ownerId, draft.prayerId);
  if (!parseTestimonyDraftRow({ key, value: raw })) refuse('testimony-draft-malformed');
  void store.put({ key, value: raw });
  return { raw, draft };
}

/** Read-only: the draft slot for one prayer (absent, a draft, or a foreign row). */
export async function readTestimonyDraftSlot(ownerId: string, prayerId: string): Promise<{
  raw: string | null; draft: TestimonyDraftV1 | null; foreign: boolean;
}> {
  const db = await getDb();
  const row = await db.get(APP_META_STORE, testimonyDraftKey(ownerId, prayerId));
  if (row === undefined) return { raw: null, draft: null, foreign: false };
  const draft = parseTestimonyDraftRow(row);
  return { raw: typeof row.value === 'string' ? row.value : null, draft, foreign: draft === null };
}

/** Read-only: every valid draft of this owner, for the Settings list. */
export async function listTestimonyDrafts(ownerId: string): Promise<TestimonyDraftSlot[]> {
  const db = await getDb();
  const rows = await db.getAll(APP_META_STORE, prefixRange(testimonyDraftOwnerPrefix(ownerId)));
  const slots: TestimonyDraftSlot[] = [];
  for (const row of rows) {
    const draft = parseTestimonyDraftRow(row);
    if (draft && draft.ownerId === ownerId && typeof row.value === 'string') slots.push({ raw: row.value, draft });
  }
  return slots;
}

/** O1: write text. Creates the draft when the caller expects none; otherwise
 * updates only the draft identity and text version the caller last read. */
export async function writeTestimonyDraft(context: ContentLifecycleContext, input: {
  prayerId: string; text: string; expected: TestimonyDraftExpectation;
  baseTestimony: string | null; baseStatus: Prayer['status'];
}): Promise<TestimonyDraftSlot> {
  assertDraftText(input.text);
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-draft-write', async (store) => {
    const slot = await readDraftSlotInTx(store, context.ownerId, input.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (!draftExpectationMatches(slot.draft, input.expected)) refuse('testimony-draft-stale');
    const next = slot.draft
      ? withText(slot.draft, input.text)
      : newDraft(context, input.prayerId, input.text, input.baseTestimony, input.baseStatus);
    return putDraft(store, next);
  });
}

/** O2: arm an unfinished action (a testimony attempt) and link the draft to it. */
export async function armTestimonyAttempt(context: ContentLifecycleContext, input: {
  prayerId: string; intent: TestimonyIntent; text: string; purpose: TestimonyDraftPurpose;
  savedTestimony: string | null; baseStatus: Prayer['status'];
  expectedDraft: TestimonyDraftExpectation; previousAttempt: PrayerAttemptV1 | null;
  recoveredAudio?: readonly RecoveredAudioCommitClaim[];
}): Promise<{ attempt: PrayerAttemptV1; draft: TestimonyDraftSlot | null }> {
  if (input.intent?.kind !== 'testimony') refuse('testimony-intent-required');
  assertDraftText(input.text);
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-attempt-arm', async (store) => {
    const previous = input.previousAttempt;
    if (previous) {
      if (previous.prayerId !== input.prayerId) refuse('testimony-attempt-prayer-mismatch');
      await assertStoredAttemptExact(store, context, previous);
    }
    const others = (await attemptsForPrayerInTx(store, context.ownerId, input.prayerId))
      .filter(attempt => attempt.commandId !== previous?.commandId &&
        (attempt.intent.kind === 'testimony' || attempt.intent.kind === 'status'));
    if (others.length > 0) refuse('testimony-attempt-independent');
    const slot = await readDraftSlotInTx(store, context.ownerId, input.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (!draftExpectationMatches(slot.draft, input.expectedDraft)) refuse('testimony-draft-stale');
    if (slot.draft?.link && (!previous || !testimonyDraftLinkedTo(slot.draft, previous))) refuse('testimony-draft-linked');
    const attempt: PrayerAttemptV1 = previous
      ? { ...previous, revision: previous.revision + 1, intent: structuredClone(input.intent),
          recoveredAudio: structuredClone([...(input.recoveredAudio ?? previous.recoveredAudio)]) }
      : { schema: 'adia-prayer-attempt-v1', ownerId: context.ownerId, commandId: mintUuidV4(), revision: 1,
          prayerId: input.prayerId, authoredAt: new Date().toISOString(), intent: structuredClone(input.intent),
          recoveredAudio: structuredClone([...(input.recoveredAudio ?? [])]) };
    const attemptKey = prayerAttemptKey(context.ownerId, attempt.commandId);
    if (!parsePrayerAttemptRow({ key: attemptKey, value: JSON.stringify(attempt) })) refuse('prayer-attempt-malformed');
    if (previous) void store.put({ key: attemptKey, value: JSON.stringify(attempt) });
    else void store.add({ key: attemptKey, value: JSON.stringify(attempt) });
    let draft: TestimonyDraftV1 | null = slot.draft ? withText(slot.draft, input.text) : null;
    if (!draft && input.text !== (input.savedTestimony ?? '')) {
      draft = newDraft(context, input.prayerId, input.text, input.savedTestimony, input.baseStatus);
    }
    const linked = draft
      ? putDraft(store, { ...draft, link: { commandId: attempt.commandId, attemptRevision: attempt.revision,
          textRevision: draft.textRevision, purpose: input.purpose } })
      : null;
    return { attempt, draft: linked };
  });
}

/**
 * O10: re-arm an UNDETERMINED draft (plan section 5.5) the user explicitly
 * chose to save: a draft whose link names an attempt that no longer exists.
 * In ONE transaction it checks that the slot still holds exactly the bytes the
 * user was shown, that the draft is this owner's and still linked, that no
 * attempt row exists under the linked command, and that the prayer has no
 * other testimony or status attempt; then it adds a fresh attempt and relinks
 * the same draft (same draftId) to it with the box text. Any refusal or a
 * failed commit leaves the original draft byte for byte and creates nothing.
 */
export async function rearmUndeterminedTestimonyDraft(context: ContentLifecycleContext, input: {
  prayerId: string; shownDraftRaw: string; intent: TestimonyIntent; text: string;
  purpose: TestimonyDraftPurpose; recoveredAudio?: readonly RecoveredAudioCommitClaim[];
}): Promise<{ attempt: PrayerAttemptV1; draft: TestimonyDraftSlot }> {
  if (input.intent?.kind !== 'testimony') refuse('testimony-intent-required');
  if (typeof input.shownDraftRaw !== 'string') refuse('testimony-draft-stale');
  assertDraftText(input.text);
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-draft-rearm', async (store) => {
    const slot = await readDraftSlotInTx(store, context.ownerId, input.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (!slot.draft || slot.raw !== input.shownDraftRaw) refuse('testimony-draft-stale');
    const draft = slot.draft;
    if (draft.ownerId !== context.ownerId || draft.prayerId !== input.prayerId) refuse('testimony-draft-foreign');
    if (!draft.link) refuse('testimony-attempt-not-linked');
    // The linked attempt must be absent: any row under its key, parseable or
    // not, means the draft is not UNDETERMINED.
    if (await store.get(prayerAttemptKey(context.ownerId, draft.link.commandId)) !== undefined) {
      refuse('testimony-draft-linked');
    }
    const others = (await attemptsForPrayerInTx(store, context.ownerId, input.prayerId))
      .filter(attempt => attempt.intent.kind === 'testimony' || attempt.intent.kind === 'status');
    if (others.length > 0) refuse('testimony-attempt-independent');
    const attempt: PrayerAttemptV1 = {
      schema: 'adia-prayer-attempt-v1', ownerId: context.ownerId, commandId: mintUuidV4(), revision: 1,
      prayerId: input.prayerId, authoredAt: new Date().toISOString(), intent: structuredClone(input.intent),
      recoveredAudio: structuredClone([...(input.recoveredAudio ?? [])]),
    };
    // Both rows are built and validated before any write is queued, so a
    // refusal never follows a queued write.
    const attemptKey = prayerAttemptKey(context.ownerId, attempt.commandId);
    const attemptRaw = JSON.stringify(attempt);
    if (!parsePrayerAttemptRow({ key: attemptKey, value: attemptRaw })) refuse('prayer-attempt-malformed');
    const next = withText(draft, input.text);
    const linkedDraft: TestimonyDraftV1 = { ...next, link: { commandId: attempt.commandId,
      attemptRevision: attempt.revision, textRevision: next.textRevision, purpose: input.purpose } };
    const draftKey = testimonyDraftKey(linkedDraft.ownerId, linkedDraft.prayerId);
    const draftRaw = serializeTestimonyDraft(linkedDraft);
    if (!parseTestimonyDraftRow({ key: draftKey, value: draftRaw })) refuse('testimony-draft-malformed');
    // Every queued request promise is observed here (account-change recording
    // plan rev 4.1, section 10): a synchronous throw after a write was queued,
    // including from the second write, still observes the first before it
    // propagates; a rejected request is rethrown. The shared helper then aborts
    // the transaction, and `tx.done` stays the authority for a later abort.
    const writes: Promise<unknown>[] = [];
    const queueWrite = (issue: () => Promise<unknown>) => { writes.push(issue()); };
    try {
      queueWrite(() => store.add({ key: attemptKey, value: attemptRaw }));
      queueWrite(() => store.put({ key: draftKey, value: draftRaw }));
    } catch (error) {
      void Promise.allSettled(writes);
      throw error;
    }
    const failed = (await Promise.allSettled(writes)).find(
      (outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');
    if (failed) throw failed.reason;
    return { attempt, draft: { raw: draftRaw, draft: linkedDraft } };
  });
}

/** O4: withdraw an unfinished action whose draft is LINKED to it. The box text,
 * when given, is written in the same transaction. */
export async function withdrawLinkedTestimonyAttempt(context: ContentLifecycleContext, input: {
  attempt: PrayerAttemptV1; expectedDraft: NonNullable<TestimonyDraftExpectation>; text?: string;
}): Promise<TestimonyDraftSlot> {
  if (input.text !== undefined) assertDraftText(input.text);
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-attempt-withdraw', async (store) => {
    await assertStoredAttemptExact(store, context, input.attempt);
    const slot = await readDraftSlotInTx(store, context.ownerId, input.attempt.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (!draftExpectationMatches(slot.draft, input.expectedDraft)) refuse('testimony-draft-stale');
    if (!slot.draft || !testimonyDraftLinkedTo(slot.draft, input.attempt)) refuse('testimony-attempt-not-linked');
    void store.delete(prayerAttemptKey(context.ownerId, input.attempt.commandId));
    const unlinked = { ...slot.draft, link: null };
    return putDraft(store, input.text === undefined ? unlinked : withText(unlinked, input.text));
  });
}

/** O5: discard exactly the draft the user was shown; never touches an attempt. */
export async function discardTestimonyDraft(context: ContentLifecycleContext, input: {
  prayerId: string; shownRaw: string;
}): Promise<'discarded' | 'absent'> {
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-draft-discard', async (store) => {
    const slot = await readDraftSlotInTx(store, context.ownerId, input.prayerId);
    if (slot.raw === null && !slot.foreign) return 'absent';
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (slot.raw !== input.shownRaw) refuse('testimony-draft-stale');
    const attempts = await attemptsForPrayerInTx(store, context.ownerId, input.prayerId);
    if (attempts.some(attempt => testimonyDraftLinkedTo(slot.draft!, attempt))) refuse('testimony-draft-linked');
    void store.delete(testimonyDraftKey(context.ownerId, input.prayerId));
    return 'discarded';
  });
}

/** O8: discard an unfinished save together with its draft (dialog only). */
export async function discardTestimonySaveWithDraft(context: ContentLifecycleContext, input: {
  attempt: PrayerAttemptV1; shownDraftRaw: string;
}): Promise<void> {
  await runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-save-discard', async (store) => {
    await assertStoredAttemptExact(store, context, input.attempt);
    const slot = await readDraftSlotInTx(store, context.ownerId, input.attempt.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (slot.raw !== input.shownDraftRaw) refuse('testimony-draft-stale');
    if (!slot.draft || !testimonyDraftLinkedTo(slot.draft, input.attempt) || slot.draft.link?.purpose !== 'save') {
      refuse('testimony-attempt-not-linked');
    }
    void store.delete(prayerAttemptKey(context.ownerId, input.attempt.commandId));
    void store.delete(testimonyDraftKey(context.ownerId, input.attempt.prayerId));
  });
}

/** O9: withdraw an unfinished action that no draft is linked to (section 3.4),
 * writing any text that must be kept in the same transaction. The draft slot
 * must hold exactly what the caller last read; a foreign row or a draft linked
 * to any action is refused even when its bytes match. */
export async function withdrawUnlinkedTestimonyAttempt(context: ContentLifecycleContext, input: {
  attempt: PrayerAttemptV1; expectedDraftRaw: string | null; text: string;
  savedTestimony: string | null; baseStatus: Prayer['status'];
}): Promise<TestimonyDraftSlot | null> {
  assertDraftText(input.text);
  return runTestimonyDraftTransaction(context, 'content-lifecycle:testimony-attempt-withdraw-unlinked', async (store) => {
    await assertStoredAttemptExact(store, context, input.attempt);
    const slot = await readDraftSlotInTx(store, context.ownerId, input.attempt.prayerId);
    if (slot.foreign) refuse('testimony-draft-foreign');
    if (slot.raw !== input.expectedDraftRaw) refuse('testimony-draft-stale');
    if (slot.draft?.link) refuse('testimony-draft-linked');
    void store.delete(prayerAttemptKey(context.ownerId, input.attempt.commandId));
    if (input.text === (input.savedTestimony ?? '')) return slot.draft ? { raw: slot.raw!, draft: slot.draft } : null;
    const next = slot.draft
      ? withText(slot.draft, input.text)
      : newDraft(context, input.attempt.prayerId, input.text, input.savedTestimony, input.baseStatus);
    return putDraft(store, next);
  });
}

/** Auth is freshly validated by the caller on explicit reload Resume; only the
 * durable owner, command identity and original expectations survive reload. */
export async function authorPrayerAttempt(
  context: ContentLifecycleContext,
  attempt: PrayerAttemptV1,
): Promise<PrayerLocalCommitResult> {
  assertLifecycleWrite('content-lifecycle:author-prayer-attempt');
  assertLifecycleContext(context);
  if (attempt.ownerId !== context.ownerId) throw new Error('prayer-attempt-owner-mismatch');
  for (let retry = 0; retry < MAX_AUTHOR_RETRIES; retry += 1) {
    const db = await getDb();
    const [row, snapshot, conflict, projection] = await Promise.all([
      db.get(APP_META_STORE, prayerAttemptKey(context.ownerId, attempt.commandId)),
      db.get(CONTENT_ENTITIES_STORE, entityKey(context.ownerId, 'prayer', attempt.prayerId)),
      db.get(CONTENT_CONFLICTS_STORE, entityKey(context.ownerId, 'prayer', attempt.prayerId)),
      db.get(PRAYERS_STORE, attempt.prayerId),
    ]);
    if (!row) return { kind: 'not-pending' };
    if (row.value !== JSON.stringify(attempt)) throw new Error('prayer-attempt-revision-stale');
    if (conflict || snapshot?.heldReason || snapshot?.quarantineReason) throw new PrayerIntentConflictError();
    let current: Prayer | undefined;
    let next: Prayer;
    if (attempt.intent.kind === 'create') {
      if (snapshot || projection || await db.get(KEEPSAKE_FENCES_STORE, attempt.prayerId)) throw new PrayerIntentConflictError();
      next = attempt.intent.prayer;
      if (next.id !== attempt.prayerId || next.pendingTranscriptionId) throw new PrayerIntentConflictError();
    } else {
      if (snapshot?.pendingDeleteAt || (snapshot && !snapshot.visibleDocument)) throw new PrayerIntentConflictError();
      // The only no-entity edit is an explicitly linked typed audio rescue.
      const typedRescue = attempt.intent.kind === 'raw' && attempt.intent.pendingQueueId &&
        projection?.pendingTranscriptionId === attempt.intent.pendingQueueId;
      if (!snapshot?.visibleDocument && !typedRescue) throw new PrayerIntentConflictError();
      current = snapshot?.visibleDocument
        ? prayerFromCompleteDocument(snapshot.visibleDocument as CompletePrayerDocument, projection)
        : projection;
      if (!current) throw new PrayerIntentConflictError();
      next = applyPrayerIntent(current, attempt.intent, attempt.authoredAt);
    }
    const document = completePrayerDocument(next);
    const expectedBaseRevision = snapshot?.localBaseRevision ?? null;
    // A visible pending restore carries explicit prior authorization. Ordinary
    // missing/tombstoned documents were rejected above; no restoration is inferred.
    const opKind = expectedBaseRevision === null ? 'create'
      : snapshot?.installedEnvelope?.payload.kind === 'tombstone' ? 'restore' : 'update';
    const mutation = buildLiveMutation(context, 'prayer', document, opKind,
      incrementDecimalBigint(snapshot?.mutationSeq ?? '0'), expectedBaseRevision, attempt.commandId);
    const tuple = await freezeTuple(context, mutation);
    if (await commitAuthoredMutation(context, snapshot, mutation, tuple, document, false,
      { expected: current, next }, attempt.recoveredAudio, attempt)) {
      notifyPrayerAttemptsChanged();
      return { kind: 'committed', prayer: prayerFromCompleteDocument(document, next),
        opId: tuple.opId, ownerId: context.ownerId, authEpoch: context.authEpoch,
        consumedAudio: [...attempt.recoveredAudio] };
    }
  }
  throw new Error('content-author-retry-exhausted');
}

/** Prayer full-document overwrites are closed. Only legacy bootstrap below
 * and isolated restore probes may use the internal compatibility seam. */
export async function authorLiveMutation(
  context: ContentLifecycleContext, entityType: ContentEntityType,
  document: CompleteLiveDocument, prayerLocalOnly?: Prayer,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<DurablePendingContentTupleV1> {
  if (entityType === 'prayer' && !isRestoreBypassActive()) throw new Error('prayer-command-required');
  return authorLiveMutationInternal(context, entityType, document, prayerLocalOnly, recoveredAudio);
}

export async function authorExplicitPrayerRestore(
  context: ContentLifecycleContext, prayer: Prayer, deletedTuple: DurablePendingContentTupleV1,
): Promise<Prayer> {
  assertLifecycleWrite('content-lifecycle:prayer-explicit-restore');
  assertLifecycleContext(context);
  if (deletedTuple.ownerId !== context.ownerId || deletedTuple.entityId !== prayer.id) throw new PrayerIntentConflictError();
  for (let retry = 0; retry < MAX_AUTHOR_RETRIES; retry += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, 'prayer', prayer.id);
    const [snapshot, conflict, projection] = await Promise.all([db.get(CONTENT_ENTITIES_STORE,key), db.get(CONTENT_CONFLICTS_STORE,key), db.get(PRAYERS_STORE,prayer.id)]);
    if (conflict || snapshot?.visibleDocument || (!snapshot && projection) || snapshot?.heldReason || snapshot?.quarantineReason) throw new PrayerIntentConflictError();
    const pendingLineage = snapshot?.pending?.opId === deletedTuple.opId;
    const acceptedLineage = snapshot?.installedEnvelope?.payload.kind === 'tombstone' && snapshot.installedEnvelope.opId === deletedTuple.opId;
    const canceledCreate = !snapshot && deletedTuple.opKind === 'create' && deletedTuple.expectedBaseRevision === null;
    // Cancelled local restore returns to the exact previously installed tombstone.
    const canceledRestore = snapshot?.installedEnvelope?.payload.kind === 'tombstone' && !snapshot.pending && deletedTuple.opKind === 'restore' && snapshot.localBaseRevision === deletedTuple.expectedBaseRevision;
    if (!pendingLineage && !acceptedLineage && !canceledCreate && !canceledRestore) throw new PrayerIntentConflictError();
    const next = { ...prayer };
    const document = completePrayerDocument(next);
    const base = snapshot?.localBaseRevision ?? null;
    const opKind = base === null ? 'create' : snapshot?.installedEnvelope?.payload.kind === 'tombstone' ? 'restore' : 'update';
    const mutation = buildLiveMutation(context, 'prayer', document, opKind, incrementDecimalBigint(snapshot?.mutationSeq ?? '0'), base, mintUuidV4());
    const tuple = await freezeTuple(context, mutation);
    if (await commitAuthoredMutation(context, snapshot, mutation, tuple, document, false, { next })) return next;
  }
  throw new Error('content-author-retry-exhausted');
}

async function authorLiveMutationInternal(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  document: CompleteLiveDocument,
  prayerLocalOnly?: Prayer,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<DurablePendingContentTupleV1> {
  assertLifecycleWrite('content-lifecycle:author-live');
  assertLifecycleContext(context);
  if (document.id === '') throw new Error('content-entity-id-empty');
  if (
    recoveredAudio.length > 0 &&
    (
      (entityType === 'prayer' && !prayerLocalOnly) ||
      (entityType === 'bookmark' &&
        recoveredAudio.some((claim) => claim.recoveryKind !== 'inline-bookmark')) ||
      entityType === 'highlight'
    )
  ) {
    throw new Error('recovered-audio-prayer-projection-required');
  }
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, entityType, document.id);
    const [snapshot, conflict] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
    ]);
    if (conflict) throw new Error('content-conflict-resolution-required');
    if (snapshot?.heldReason || snapshot?.quarantineReason) {
      throw new Error('content-mutation-parked');
    }
    const mutationSeq = incrementDecimalBigint(snapshot?.mutationSeq ?? '0');
    const expectedBaseRevision = snapshot?.localBaseRevision ?? null;
    const opKind =
      expectedBaseRevision === null
        ? 'create'
        : snapshot?.installedEnvelope?.payload.kind === 'tombstone'
          ? 'restore'
          : 'update';
    const mutation = buildLiveMutation(
      context,
      entityType,
      document,
      opKind,
      mutationSeq,
      expectedBaseRevision,
      mintUuidV4(),
    );
    const tuple = await freezeTuple(context, mutation);
    if (
      await commitAuthoredMutation(
        context,
        snapshot,
        mutation,
        tuple,
        document,
        false,
        prayerLocalOnly ? { next: prayerLocalOnly } : undefined,
        recoveredAudio,
      )
    ) {
      return tuple;
    }
  }
  throw new Error('content-author-retry-exhausted');
}

export interface RecoveredPrayerTranscriptionCommit {
  prayer: Prayer;
  storagePath?: string;
  destinationOpId: string;
}

/**
 * Turn a pending enhanced-voice prayer into an ordinary lifecycle mutation.
 * The visible prayer, immutable pending tuple, entity state, and audio-row
 * consumption commit in one transaction. A crash or stale tab therefore
 * cannot produce an unsynced recovered prayer or resurrect consumed audio.
 */
export async function authorRecoveredPrayerTranscription(
  context: ContentLifecycleContext,
  queueItemId: string,
): Promise<RecoveredPrayerTranscriptionCommit | null> {
  assertLifecycleWrite('content-lifecycle:author-recovered-prayer');
  assertLifecycleContext(context);
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const queue = await db.get(AUDIO_QUEUE_STORE, queueItemId);
    const recovered = queue?.transcribedText?.trim() ?? '';
    if (
      !queue ||
      !queue.prayerId ||
      !recovered ||
      (!enhancedVoiceAllowed() && audioQueueRowCarriesBytes(queue)) ||
      (queue.recoveryKind != null && queue.recoveryKind !== 'new-prayer')
    ) return null;

    const key = entityKey(context.ownerId, 'prayer', queue.prayerId);
    const [snapshot, conflict, projection] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
      db.get(PRAYERS_STORE, queue.prayerId),
    ]);
    if (conflict) throw new Error('content-conflict-resolution-required');
    if (snapshot?.heldReason || snapshot?.quarantineReason) {
      throw new Error('content-mutation-parked');
    }
    if (!projection || projection.pendingTranscriptionId !== queueItemId) return null;

    const next: Prayer = {
      ...projection,
      rawText: mergeRecoveredVoiceText(projection.rawText, recovered),
      pendingTranscriptionId: undefined,
      transcriptionFailed: undefined,
    };
    const document = completePrayerDocument(next);
    const mutationSeq = incrementDecimalBigint(snapshot?.mutationSeq ?? '0');
    const expectedBaseRevision = snapshot?.localBaseRevision ?? null;
    const opKind =
      expectedBaseRevision === null
        ? 'create'
        : snapshot?.installedEnvelope?.payload.kind === 'tombstone'
          ? 'restore'
          : 'update';
    const mutation = buildLiveMutation(
      context,
      'prayer',
      document,
      opKind,
      mutationSeq,
      expectedBaseRevision,
      mintUuidV4(),
    );
    const tuple = await freezeTuple(context, mutation);
    if (
      await commitAuthoredMutation(
        context,
        snapshot,
        mutation,
        tuple,
        document,
        false,
        { expected: projection, next },
        [{
          queueItemId,
          recoveryKind: 'new-prayer',
          expectedTranscribedText: recovered,
        }],
      )
    ) {
      return {
        prayer: prayerFromCompleteDocument(document, next),
        storagePath: queue.storagePath,
        destinationOpId: tuple.opId,
      };
    }
  }
  throw new Error('content-author-retry-exhausted');
}

/**
 * Author an enrichment result from the authoritative lifecycle entity and its
 * local projection. The optimistic computation is accepted only when the
 * entity snapshot and local work token are still identical in the commit
 * transaction. A local edit or installed remote winner forces a fresh read.
 */
export async function authorPrayerMutationCas(
  context: ContentLifecycleContext,
  prayerId: string,
  guard: (current: Prayer) => boolean,
  computeNext: (current: Prayer) => Prayer,
): Promise<Prayer | null> {
  assertLifecycleWrite('content-lifecycle:author-prayer-cas');
  assertLifecycleContext(context);
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, 'prayer', prayerId);
    const [snapshot, conflict, projection] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
      db.get(PRAYERS_STORE, prayerId),
    ]);
    if (conflict) throw new Error('content-conflict-resolution-required');
    if (snapshot?.heldReason || snapshot?.quarantineReason) {
      throw new Error('content-mutation-parked');
    }
    if (!snapshot?.visibleDocument || snapshot.entityType !== 'prayer') return null;
    const current = prayerFromCompleteDocument(
      snapshot.visibleDocument as CompletePrayerDocument,
      projection,
    );
    if (!guard(current)) return null;
    const next = computeNext(current);
    if (next.id !== prayerId) throw new Error('content-entity-id-mismatch');
    const document = completePrayerDocument(next);
    const mutationSeq = incrementDecimalBigint(snapshot.mutationSeq);
    const expectedBaseRevision = snapshot.localBaseRevision;
    const opKind =
      expectedBaseRevision === null
        ? 'create'
        : snapshot.installedEnvelope?.payload.kind === 'tombstone'
          ? 'restore'
          : 'update';
    const mutation = buildLiveMutation(
      context,
      'prayer',
      document,
      opKind,
      mutationSeq,
      expectedBaseRevision,
      mintUuidV4(),
    );
    const tuple = await freezeTuple(context, mutation);
    if (
      await commitAuthoredMutation(
        context,
        snapshot,
        mutation,
        tuple,
        document,
        false,
        { expected: current, next },
      )
    ) {
      return prayerFromCompleteDocument(document, next);
    }
  }
  throw new Error('content-author-retry-exhausted');
}

/**
 * Compute a complete live document from the authoritative lifecycle snapshot,
 * then commit only if that snapshot is still exact. This is the bookmark /
 * highlight equivalent of authorPrayerMutationCas: UI state is an input hint,
 * never permission to silently rebase stale fields onto an unseen remote tip.
 */
export async function authorLiveDocumentMutationCas(
  context: ContentLifecycleContext,
  entityType: Exclude<ContentEntityType, 'prayer'>,
  entityId: string,
  computeNext: (
    current: CompleteLiveDocument | null,
    installed: OrdinaryContentEnvelopeV1 | null,
    entity: LocalContentEntityV1 | null,
  ) => CompleteLiveDocument | null,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<CompleteLiveDocument | null> {
  assertLifecycleWrite('content-lifecycle:author-document-cas');
  assertLifecycleContext(context);
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, entityType, entityId);
    const [snapshot, conflict] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
    ]);
    if (conflict) throw new Error('content-conflict-resolution-required');
    if (snapshot?.heldReason || snapshot?.quarantineReason) {
      throw new Error('content-mutation-parked');
    }
    const document = computeNext(
      snapshot?.visibleDocument ?? null,
      snapshot?.installedEnvelope ?? null,
      snapshot ?? null,
    );
    if (document === null) return null;
    if (document.id !== entityId) throw new Error('content-entity-id-mismatch');
    const mutationSeq = incrementDecimalBigint(snapshot?.mutationSeq ?? '0');
    const expectedBaseRevision = snapshot?.localBaseRevision ?? null;
    const opKind =
      expectedBaseRevision === null
        ? 'create'
        : snapshot?.installedEnvelope?.payload.kind === 'tombstone'
          ? 'restore'
          : 'update';
    const mutation = buildLiveMutation(
      context,
      entityType,
      document,
      opKind,
      mutationSeq,
      expectedBaseRevision,
      mintUuidV4(),
    );
    const tuple = await freezeTuple(context, mutation);
    if (
      await commitAuthoredMutation(
        context,
        snapshot,
        mutation,
        tuple,
        document,
        false,
        undefined,
        recoveredAudio,
      )
    ) {
      return document;
    }
  }
  throw new Error('content-author-retry-exhausted');
}

/** Note-only bookmark edit bound to the exact document shown when edit began. */
export async function authorBookmarkNoteMutation(
  context: ContentLifecycleContext,
  fallback: CompleteBookmarkDocument,
  note: string,
  updatedAt: string,
  expectedUpdatedAt: string,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<CompleteBookmarkDocument> {
  const document = await authorLiveDocumentMutationCas(
    context,
    'bookmark',
    fallback.id,
    (current, _installed, entity) => {
      if (current === null) {
        // Only a truly unadopted legacy row may use the fallback. A lifecycle
        // tombstone or pending delete is authoritative and must never become an
        // implicit restore because a stale editor finally saved.
        if (entity !== null) throw new BookmarkNotFoundError();
        if (fallback.updatedAt !== expectedUpdatedAt) throw new BookmarkStaleEditError();
        return { ...fallback, note, updatedAt };
      }
      const bookmark = current as CompleteBookmarkDocument;
      if (bookmark.updatedAt !== expectedUpdatedAt) throw new BookmarkStaleEditError();
      return { ...bookmark, note, updatedAt };
    },
    recoveredAudio,
  );
  if (!document) throw new BookmarkNotFoundError();
  return document as CompleteBookmarkDocument;
}

/**
 * Compatibility bookmark-create lane. An exact retry is a no-op; an existing
 * differing live document or tombstoned identity is rejected. Updates must use
 * an authoritative field-level CAS so stale UI documents cannot overwrite an
 * unseen winner.
 */
export async function authorBookmarkCreateOnlyMutation(
  context: ContentLifecycleContext,
  document: CompleteBookmarkDocument,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<'created' | 'idempotent'> {
  const committed = await authorLiveDocumentMutationCas(
    context,
    'bookmark',
    document.id,
    (current, installed) => {
      if (current !== null) {
        if (bytesEqual(cj1Bytes(current), cj1Bytes(document))) return null;
        throw new Error('bookmark-create-conflicts-existing');
      }
      if (installed !== null) throw new Error('bookmark-create-conflicts-existing');
      return document;
    },
    recoveredAudio,
  );
  return committed === null ? 'idempotent' : 'created';
}

export type HighlightLiveBatchEntry = {
  entityId: string;
  computeNext: (
    current: CompleteHighlightDocument | null,
    installed: OrdinaryContentEnvelopeV1 | null,
  ) => CompleteHighlightDocument;
};

/** All highlight documents install, project, and enqueue in one IDB transaction. */
export async function authorHighlightLiveBatchCas(
  context: ContentLifecycleContext,
  entries: HighlightLiveBatchEntry[],
): Promise<CompleteHighlightDocument[]> {
  assertLifecycleWrite('content-lifecycle:author-highlight-batch');
  assertLifecycleContext(context);
  if (entries.length === 0) return [];
  if (new Set(entries.map((entry) => entry.entityId)).size !== entries.length) {
    throw new Error('content-batch-duplicate-entity');
  }
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const snapshots = await Promise.all(entries.map(async (entry) => {
      const key = entityKey(context.ownerId, 'highlight', entry.entityId);
      const [entity, conflict] = await Promise.all([
        db.get(CONTENT_ENTITIES_STORE, key),
        db.get(CONTENT_CONFLICTS_STORE, key),
      ]);
      if (conflict) throw new Error('content-conflict-resolution-required');
      if (entity?.heldReason || entity?.quarantineReason) {
        throw new Error('content-mutation-parked');
      }
      const current = entity?.visibleDocument as CompleteHighlightDocument | null | undefined;
      const document = entry.computeNext(current ?? null, entity?.installedEnvelope ?? null);
      if (document.id !== entry.entityId) throw new Error('content-entity-id-mismatch');
      const expectedBaseRevision = entity?.localBaseRevision ?? null;
      const mutation = buildLiveMutation(
        context,
        'highlight',
        document,
        expectedBaseRevision === null
          ? 'create'
          : entity?.installedEnvelope?.payload.kind === 'tombstone'
            ? 'restore'
            : 'update',
        incrementDecimalBigint(entity?.mutationSeq ?? '0'),
        expectedBaseRevision,
        mintUuidV4(),
      );
      return { entry, entity, document, mutation, tuple: await freezeTuple(context, mutation) };
    }));

    const tx = db.transaction(
      [CONTENT_ENTITIES_STORE, CONTENT_PENDING_STORE, CONTENT_CONFLICTS_STORE, BIBLE_HIGHLIGHTS_STORE, APP_META_STORE],
      'readwrite',
    );
    const entityStore = tx.objectStore(CONTENT_ENTITIES_STORE);
    const pendingStore = tx.objectStore(CONTENT_PENDING_STORE);
    const conflictStore = tx.objectStore(CONTENT_CONFLICTS_STORE);
    let retry = false;
    for (const item of snapshots) {
      const key = entityKey(context.ownerId, 'highlight', item.entry.entityId);
      const [current, conflict, previousPending] = await Promise.all([
        entityStore.get(key),
        conflictStore.get(key),
        item.entity?.pending
          ? pendingStore.get([context.ownerId, item.entity.pending.opId])
          : Promise.resolve(undefined),
      ]);
      if (!entitySnapshotEqual(current, item.entity) || conflict) {
        retry = true;
        break;
      }
      const stored: StoredPendingContentOperationV1 = {
        ownerId: context.ownerId,
        opId: item.tuple.opId,
        entityType: 'highlight',
        entityId: item.entry.entityId,
        tuple: item.tuple,
        mutation: item.mutation,
        prayerFenceMutationSeq: null,
        dispatchState: 'pending',
      };
      await putLiveProjection(
        tx as unknown as Parameters<typeof putLiveProjection>[0],
        'highlight',
        item.document,
      );
      void pendingStore.put(stored);
      if (
        current?.pending &&
        previousPending?.dispatchState === 'pending' &&
        pendingIdentityEqual(current.pending, previousPending.tuple)
      ) {
        void pendingStore.delete([context.ownerId, previousPending.opId]);
      }
      void entityStore.put({
        ownerId: context.ownerId,
        entityType: 'highlight',
        entityId: item.entry.entityId,
        state: 'dirty',
        installedEnvelope: current?.installedEnvelope ?? null,
        visibleDocument: item.document,
        pending: item.tuple,
        pendingDeleteAt: null,
        mutationSeq: item.tuple.mutationSeq,
        localBaseRevision: current?.localBaseRevision ?? null,
        heldReason: null,
        quarantineReason: null,
        recovery: 'noop',
      } satisfies LocalContentEntityV1);
    }
    if (retry) {
      await abortQuietly(tx);
      continue;
    }
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    notifyContentCommitted({ ownerId: context.ownerId });
    return snapshots.map((item) => item.document);
  }
  throw new Error('content-author-retry-exhausted');
}

type PlannedHighlightDelete =
  | {
      kind: 'mutation';
      entityId: string;
      snapshot: LocalContentEntityV1;
      previousPending: StoredPendingContentOperationV1 | undefined;
      mutation: ContentMutationV1;
      tuple: DurablePendingContentTupleV1;
      tombstonedAt: string;
    }
  | {
      kind: 'cancel-local-create';
      entityId: string;
      snapshot: LocalContentEntityV1;
      previousPending: StoredPendingContentOperationV1;
      tombstonedAt: string;
    }
  | {
      kind: 'cancel-local-restore';
      entityId: string;
      snapshot: LocalContentEntityV1;
      previousPending: StoredPendingContentOperationV1;
      tombstonedAt: string;
    }
  | {
      kind: 'delete-after-accept';
      entityId: string;
      snapshot: LocalContentEntityV1;
      previousPending: StoredPendingContentOperationV1;
      tombstonedAt: string;
    };

/** Atomically authors/cancels every highlight delete in one local transaction. */
export async function authorHighlightDeleteBatch(
  context: ContentLifecycleContext,
  ids: string[],
  tombstonedAt: string,
): Promise<string[]> {
  assertLifecycleWrite('content-lifecycle:author-highlight-delete-batch');
  assertLifecycleContext(context);
  const entityIds = [...new Set(ids)];
  if (entityIds.length === 0) return [];
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const candidates = await Promise.all(entityIds.map(async (
      entityId,
    ): Promise<PlannedHighlightDelete | null> => {
      const key = entityKey(context.ownerId, 'highlight', entityId);
      const [snapshot, conflict] = await Promise.all([
        db.get(CONTENT_ENTITIES_STORE, key),
        db.get(CONTENT_CONFLICTS_STORE, key),
      ]);
      if (conflict) throw new Error('content-conflict-resolution-required');
      if (!snapshot) return null;
      if (snapshot.heldReason || snapshot.quarantineReason) {
        throw new Error('content-mutation-parked');
      }
      const previousPending = snapshot.pending
        ? await db.get(CONTENT_PENDING_STORE, [context.ownerId, snapshot.pending.opId])
        : undefined;
      const installed = snapshot.installedEnvelope;
      if (!installed || installed.payload.kind !== 'live') {
        if (!snapshot.pending || !previousPending) {
          throw new Error('content-delete-requires-accepted-live');
        }
        if (previousPending.dispatchState === 'pending') {
          if (isCancelablePendingRestore(snapshot, previousPending)) {
            return {
              kind: 'cancel-local-restore', entityId, snapshot, previousPending, tombstonedAt,
            } satisfies PlannedHighlightDelete;
          }
          if (!isCancelablePendingCreate(snapshot, previousPending)) {
            throw new Error('content-delete-pending-base-invalid');
          }
          return {
            kind: 'cancel-local-create', entityId, snapshot, previousPending, tombstonedAt,
          } satisfies PlannedHighlightDelete;
        }
        return {
          kind: 'delete-after-accept', entityId, snapshot, previousPending, tombstonedAt,
        } satisfies PlannedHighlightDelete;
      }
      const mutation: ContentMutationV1 = {
        schema: 'adia-content-mutation-v1',
        protocolVersion: 1,
        ownerId: context.ownerId,
        entityType: 'highlight',
        entityId,
        opId: mintUuidV4(),
        opKind: 'delete',
        mutationSeq: incrementDecimalBigint(snapshot.mutationSeq),
        expectedBaseRevision: installed.contentRevision,
        payload: {
          kind: 'tombstone-intent',
          predecessorLiveDigest: installed.contentDigest,
          predecessorLiveSnapshot: installed.payload.document,
          tombstonedAt,
        },
      };
      return {
        kind: 'mutation',
        entityId,
        snapshot,
        previousPending,
        mutation,
        tuple: await freezeTuple(context, mutation),
        tombstonedAt,
      } satisfies PlannedHighlightDelete;
    }));
    if (candidates.some((item) => item === null)) {
      throw new Error('highlight-not-found');
    }
    const planned = candidates as PlannedHighlightDelete[];

    const tx = db.transaction(
      [CONTENT_ENTITIES_STORE, CONTENT_PENDING_STORE, CONTENT_CONFLICTS_STORE, BIBLE_HIGHLIGHTS_STORE, APP_META_STORE],
      'readwrite',
    );
    const entities = tx.objectStore(CONTENT_ENTITIES_STORE);
    const pending = tx.objectStore(CONTENT_PENDING_STORE);
    const conflicts = tx.objectStore(CONTENT_CONFLICTS_STORE);
    let retry = false;
    for (const item of planned) {
      const key = entityKey(context.ownerId, 'highlight', item.entityId);
      const [current, conflict, currentPending] = await Promise.all([
        entities.get(key),
        conflicts.get(key),
        pending.get([context.ownerId, item.previousPending?.opId ?? '']),
      ]);
      if (
        !entitySnapshotEqual(current, item.snapshot) ||
        conflict ||
        (item.previousPending &&
          (!currentPending || !pendingIdentityEqual(currentPending.tuple, item.previousPending.tuple)))
      ) {
        retry = true;
        break;
      }
      if (item.kind === 'cancel-local-create') {
        void pending.delete([context.ownerId, item.previousPending.opId]);
        void entities.delete(key);
        void tx.objectStore(BIBLE_HIGHLIGHTS_STORE).delete(item.entityId);
        continue;
      }
      if (item.kind === 'cancel-local-restore') {
        const installedTombstone = item.snapshot.installedEnvelope;
        if (!installedTombstone || installedTombstone.payload.kind !== 'tombstone') {
          retry = true;
          break;
        }
        void pending.delete([context.ownerId, item.previousPending.opId]);
        await putTombstoneProjection(
          tx as unknown as Parameters<typeof putTombstoneProjection>[0],
          'highlight',
          item.entityId,
          installedTombstone.payload.tombstonedAt,
        );
        void entities.put({
          ...item.snapshot,
          state: 'tombstoned',
          installedEnvelope: installedTombstone,
          visibleDocument: null,
          pending: null,
          pendingDeleteAt: null,
          localBaseRevision: installedTombstone.contentRevision,
          recovery: 'tombstone-installed',
        });
        continue;
      }
      await putTombstoneProjection(
        tx as unknown as Parameters<typeof putTombstoneProjection>[0],
        'highlight',
        item.entityId,
        item.tombstonedAt,
      );
      if (item.kind === 'delete-after-accept') {
        void entities.put({
          ...item.snapshot,
          state: 'tombstoned',
          visibleDocument: null,
          pendingDeleteAt: item.tombstonedAt,
        });
        continue;
      }
      const stored: StoredPendingContentOperationV1 = {
        ownerId: context.ownerId,
        opId: item.tuple.opId,
        entityType: 'highlight',
        entityId: item.entityId,
        tuple: item.tuple,
        mutation: item.mutation,
        prayerFenceMutationSeq: null,
        dispatchState: 'pending',
      };
      void pending.put(stored);
      if (currentPending?.dispatchState === 'pending') {
        void pending.delete([context.ownerId, currentPending.opId]);
      }
      void entities.put({
        ...item.snapshot,
        state: 'tombstoned',
        visibleDocument: null,
        pending: item.tuple,
        pendingDeleteAt: null,
        mutationSeq: item.tuple.mutationSeq,
      });
    }
    if (retry) {
      await abortQuietly(tx);
      continue;
    }
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    notifyContentCommitted({ ownerId: context.ownerId });
    return planned.map((item) => item.entityId);
  }
  throw new Error('content-author-retry-exhausted');
}

export async function authorDeleteMutation(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  entityId: string,
  tombstonedAt: string,
  expectedPrayer?: Prayer,
  expectedDocument?: CompleteLiveDocument,
): Promise<DurablePendingContentTupleV1> {
  assertLifecycleWrite('content-lifecycle:author-delete');
  if (entityType === 'prayer' && !expectedPrayer && !isRestoreBypassActive()) throw new Error('prayer-delete-expectation-required');
  assertLifecycleContext(context);
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, entityType, entityId);
    const [snapshot, conflict] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
    ]);
    if (conflict) throw new Error('content-conflict-resolution-required');
    if (expectedDocument && (!snapshot?.visibleDocument || !bytesEqual(cj1Bytes(snapshot.visibleDocument), cj1Bytes(expectedDocument)))) throw new PrayerIntentConflictError();
    if (expectedPrayer && (!snapshot?.visibleDocument || !bytesEqual(cj1Bytes(snapshot.visibleDocument), cj1Bytes(completePrayerDocument(expectedPrayer))))) throw new PrayerIntentConflictError();
    const installed = snapshot?.installedEnvelope;
    if (!snapshot) throw new Error('content-delete-requires-accepted-live');
    if (!installed || installed.payload.kind !== 'live') {
      if (!snapshot.pending) {
        throw new Error('content-delete-requires-accepted-live');
      }
      const previousPending = await db.get(
        CONTENT_PENDING_STORE,
        [context.ownerId, snapshot.pending.opId],
      );
      if (!previousPending || !pendingIdentityEqual(previousPending.tuple, snapshot.pending)) {
        throw new Error('content-delete-requires-accepted-live');
      }
      const stores = entityType === 'prayer'
        ? [
            CONTENT_ENTITIES_STORE,
            CONTENT_PENDING_STORE,
            CONTENT_CONFLICTS_STORE,
            APP_META_STORE,
            PRAYERS_STORE,
            KEEPSAKE_FENCES_STORE,
            KEEPSAKE_META_STORE,
          ] as const
        : [
            CONTENT_ENTITIES_STORE,
            CONTENT_PENDING_STORE,
            CONTENT_CONFLICTS_STORE,
            APP_META_STORE,
            projectionStore(entityType),
          ] as const;
      const tx = db.transaction(stores, 'readwrite');
      const [current, currentPending, currentConflict] = await Promise.all([
        tx.objectStore(CONTENT_ENTITIES_STORE).get(key),
        tx.objectStore(CONTENT_PENDING_STORE).get([
          context.ownerId,
          previousPending.opId,
        ]),
        tx.objectStore(CONTENT_CONFLICTS_STORE).get(key),
      ]);
      if (
        !entitySnapshotEqual(current, snapshot) ||
        !currentPending ||
        !pendingIdentityEqual(currentPending.tuple, previousPending.tuple) ||
        currentConflict
      ) {
        await abortQuietly(tx);
        continue;
      }
      if (previousPending.dispatchState === 'pending') {
        const cancelPendingRestore = isCancelablePendingRestore(snapshot, previousPending);
        if (!cancelPendingRestore && !isCancelablePendingCreate(snapshot, previousPending)) {
          await abortQuietly(tx);
          throw new Error('content-delete-pending-base-invalid');
        }
        let fenceMarks: { seq: string; epoch: string } | null = null;
        if (entityType === 'prayer') {
          fenceMarks = await bumpLifecyclePrayerFenceInTx(
            tx as unknown as Parameters<typeof bumpLifecyclePrayerFenceInTx>[0],
            entityId,
            context.ownerId,
            'local',
          );
          if (!await acknowledgeLifecyclePrayerFenceInTx(
            tx as unknown as Parameters<typeof acknowledgeLifecyclePrayerFenceInTx>[0],
            entityId,
            context.ownerId,
            fenceMarks.seq,
          )) {
            await abortQuietly(tx);
            continue;
          }
        }
        void tx.objectStore(CONTENT_PENDING_STORE).delete([
          context.ownerId,
          previousPending.opId,
        ]);
        if (cancelPendingRestore) {
          const installedTombstone = installed;
          if (!installedTombstone || installedTombstone.payload.kind !== 'tombstone') {
            await abortQuietly(tx);
            throw new Error('content-delete-pending-base-invalid');
          }
          // A pending restore is a successor of an already accepted tombstone,
          // not a fresh create. Cancel only the unsent successor and return to
          // that exact durable base so another restore remains a CAS successor
          // of the tombstone instead of becoming an unrelated create.
          await putTombstoneProjection(
            tx as unknown as Parameters<typeof putTombstoneProjection>[0],
            entityType,
            entityId,
            installedTombstone.payload.tombstonedAt,
          );
          void tx.objectStore(CONTENT_ENTITIES_STORE).put({
            ...snapshot,
            state: 'tombstoned',
            installedEnvelope: installedTombstone,
            visibleDocument: null,
            pending: null,
            pendingDeleteAt: null,
            localBaseRevision: installedTombstone.contentRevision,
            recovery: 'tombstone-installed',
          });
        } else {
          void tx.objectStore(CONTENT_ENTITIES_STORE).delete(key);
          void tx.objectStore(projectionStore(entityType)).delete(entityId);
        }
        await assertContextBeforeCommit(tx, context);
        await tx.done;
        notifyContentCommitted({ ownerId: context.ownerId, entityType, entityId });
        if (fenceMarks) { try { finalizeLifecyclePrayerFence(entityId, fenceMarks); } catch { /* durable delete succeeded */ } }
        return previousPending.tuple;
      } else {
        let fenceMarks: { seq: string; epoch: string } | null = null;
        if (entityType === 'prayer') {
          fenceMarks = await bumpLifecyclePrayerFenceInTx(
            tx as unknown as Parameters<typeof bumpLifecyclePrayerFenceInTx>[0],
            entityId,
            context.ownerId,
            'local',
          );
          void tx.objectStore(PRAYERS_STORE).delete(entityId);
          void tx.objectStore(CONTENT_PENDING_STORE).put({
            ...currentPending,
            prayerFenceMutationSeq: fenceMarks.seq,
          });
        } else {
          await putTombstoneProjection(
            tx as unknown as Parameters<typeof putTombstoneProjection>[0],
            entityType,
            entityId,
            tombstonedAt,
          );
        }
        void tx.objectStore(CONTENT_ENTITIES_STORE).put({
          ...snapshot,
          state: 'tombstoned',
          visibleDocument: null,
          pendingDeleteAt: tombstonedAt,
        });
        await assertContextBeforeCommit(tx, context);
        await tx.done;
        notifyContentCommitted({ ownerId: context.ownerId, entityType, entityId });
        if (fenceMarks) { try { finalizeLifecyclePrayerFence(entityId, fenceMarks); } catch { /* durable delete succeeded */ } }
        return previousPending.tuple;
      }
    }
    const mutation: ContentMutationV1 = {
      schema: 'adia-content-mutation-v1',
      protocolVersion: 1,
      ownerId: context.ownerId,
      entityType,
      entityId,
      opId: mintUuidV4(),
      opKind: 'delete',
      mutationSeq: incrementDecimalBigint(snapshot.mutationSeq),
      expectedBaseRevision: installed.contentRevision,
      payload: {
        kind: 'tombstone-intent',
        predecessorLiveDigest: installed.contentDigest,
        predecessorLiveSnapshot: installed.payload.document,
        tombstonedAt,
      },
    };
    const tuple = await freezeTuple(context, mutation);
    if (await commitAuthoredMutation(context, snapshot, mutation, tuple, null, false)) return tuple;
  }
  throw new Error('content-author-retry-exhausted');
}

async function authorLegacyBootstrapLive(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  document: CompleteLiveDocument,
): Promise<boolean> {
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, entityType, document.id);
    const [snapshot, conflict] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
    ]);
    if (conflict || snapshot?.pending || snapshot?.heldReason || snapshot?.quarantineReason) {
      return false;
    }
    if (
      snapshot?.visibleDocument &&
      bytesEqual(cj1Bytes(snapshot.visibleDocument), cj1Bytes(document))
    ) {
      return false;
    }
    if (!snapshot) {
      await authorLiveMutationInternal(context, entityType, document);
      return true;
    }
    // A different installed server tip must arbitrate the legacy bytes. A
    // create with null expected base is intentionally rejected, preserving
    // the complete local document as the conflict loser instead of silently
    // overwriting either side during first-run conversion.
    const mutation = buildLiveMutation(
      context,
      entityType,
      document,
      'create',
      incrementDecimalBigint(snapshot.mutationSeq),
      null,
      mintUuidV4(),
    );
    const tuple = await freezeTuple(context, mutation);
    if (await commitAuthoredMutation(context, snapshot, mutation, tuple, document, false)) {
      return true;
    }
  }
  throw new Error('content-bootstrap-retry-exhausted');
}

async function materializeLegacyProjections(
  context: ContentLifecycleContext,
  entityTypes: readonly ContentEntityType[],
): Promise<void> {
  const db = await getDb();
  for (const entityType of entityTypes) {
    const tx = db.transaction(
      [CONTENT_ENTITIES_STORE, projectionStore(entityType), APP_META_STORE],
      'readwrite',
    );
    const entities = await tx.objectStore(CONTENT_ENTITIES_STORE)
      .index('by-owner')
      .getAll(context.ownerId);
    for (const entity of entities) {
      if (entity.entityType !== entityType) continue;
      if (entity.visibleDocument) {
        await putLiveProjection(
          tx as unknown as Parameters<typeof putLiveProjection>[0],
          entityType,
          entity.visibleDocument,
        );
      } else if (entity.installedEnvelope?.payload.kind === 'tombstone') {
        await putTombstoneProjection(
          tx as unknown as Parameters<typeof putTombstoneProjection>[0],
          entityType,
          entity.entityId,
          entity.installedEnvelope.payload.tombstonedAt,
        );
      }
    }
    await assertContextBeforeCommit(tx, context);
    await tx.done;
  }
}

/**
 * Reconcile the verified-owner legacy projection captured before first pull.
 * Matching server documents are left untouched; device-only live rows become
 * durable creates; differences are forced through CAS so neither side wins
 * silently. Legacy tombstones are replayed only against an installed live tip.
 */
export async function reconcileLegacyContentBootstrap(
  context: ContentLifecycleContext,
  snapshot: LegacyContentBootstrapSnapshot | null,
): Promise<number> {
  if (!snapshot) return 0;
  assertLifecycleWrite('content-lifecycle:bootstrap-legacy');
  assertLifecycleContext(context);
  let authored = 0;
  for (const prayer of snapshot.prayers) {
    if (prayer.pendingTranscriptionId) continue;
    if (await authorLegacyBootstrapLive(context, 'prayer', completePrayerDocument(prayer))) {
      authored += 1;
    }
  }
  for (const bookmark of snapshot.bookmarks) {
    if (bookmark.deletedAt) {
      const entity = await getLocalContentEntity(context.ownerId, 'bookmark', bookmark.id);
      if (entity?.installedEnvelope?.payload.kind === 'live') {
        await authorDeleteMutation(context, 'bookmark', bookmark.id, bookmark.deletedAt);
        authored += 1;
      }
    } else if (
      await authorLegacyBootstrapLive(context, 'bookmark', completeBookmarkDocument(bookmark))
    ) {
      authored += 1;
    }
  }
  for (const highlight of snapshot.highlights) {
    if (highlight.deletedAt) {
      const entity = await getLocalContentEntity(context.ownerId, 'highlight', highlight.id);
      if (entity?.installedEnvelope?.payload.kind === 'live') {
        await authorDeleteMutation(context, 'highlight', highlight.id, highlight.deletedAt);
        authored += 1;
      }
    } else if (
      await authorLegacyBootstrapLive(context, 'highlight', completeHighlightDocument(highlight))
    ) {
      authored += 1;
    }
  }
  // A disabled source may have accumulated inbound envelopes in the shared
  // owner log without touching its legacy projection. Once enabled, replay the
  // authoritative visible lifecycle state only after its pre-pull legacy bytes
  // have been reconciled, so neither side is silently overwritten.
  await materializeLegacyProjections(context, snapshot.entityTypes);
  const db = await getDb();
  const tx = db.transaction([CONTENT_PARTITIONS_STORE, APP_META_STORE], 'readwrite');
  const partitions = tx.objectStore(CONTENT_PARTITIONS_STORE);
  const current = (await partitions.get(context.ownerId)) ?? defaultPartition(context.ownerId);
  const completed = new Set(
    current.legacyBootstrapComplete
      ? ALL_CONTENT_ENTITY_TYPES
      : current.legacyBootstrapEntityTypes ?? [],
  );
  for (const entityType of snapshot.entityTypes) completed.add(entityType);
  const legacyBootstrapEntityTypes = ALL_CONTENT_ENTITY_TYPES.filter((entityType) =>
    completed.has(entityType));
  void partitions.put({
    ...current,
    legacyBootstrapComplete:
      legacyBootstrapEntityTypes.length === ALL_CONTENT_ENTITY_TYPES.length,
    legacyBootstrapEntityTypes,
  });
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  return authored;
}

export async function markContentOperationInFlight(
  context: ContentLifecycleContext,
  opId: string,
): Promise<StoredPendingContentOperationV1> {
  assertLifecycleWrite('content-lifecycle:mark-in-flight');
  assertLifecycleContext(context);
  const db = await getDb();
  const tx = db.transaction([CONTENT_PENDING_STORE, APP_META_STORE], 'readwrite');
  const store = tx.objectStore(CONTENT_PENDING_STORE);
  const operation = await store.get([context.ownerId, opId]);
  if (!operation || !contextMatches(context, operation.tuple)) {
    await tx.done;
    throw new Error('content-pending-context-mismatch');
  }
  const next = { ...operation, dispatchState: 'in-flight' as const };
  void store.put(next);
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  return next;
}

export type ContentDispatchClaim =
  | { kind: 'in-flight'; operation: StoredPendingContentOperationV1 }
  | { kind: 'superseded' };

/** Receipt lookup may overlap a local coalesce. Never dispatch its retired bytes. */
export async function claimContentOperationForDispatch(
  context: ContentLifecycleContext,
  captured: StoredPendingContentOperationV1,
): Promise<ContentDispatchClaim> {
  assertLifecycleWrite('content-lifecycle:mark-in-flight');
  assertLifecycleContext(context);
  const mismatch = () => new Error('content-pending-context-mismatch');
  const assertOperation = (operation: StoredPendingContentOperationV1) => {
    if (!contextMatches(context, operation.tuple) ||
        operation.ownerId !== context.ownerId || operation.opId !== operation.tuple.opId ||
        operation.entityType !== operation.tuple.entityType ||
        operation.entityId !== operation.tuple.entityId ||
        !bytesEqual(cj1Bytes(operation.mutation), operation.tuple.completePendingBytes) ||
        (operation.dispatchState !== 'pending' && operation.dispatchState !== 'in-flight')) {
      throw mismatch();
    }
  };
  assertOperation(captured);
  await verifyDurablePendingTuple(captured.tuple);
  assertLifecycleContext(context);
  const db = await getDb();
  const stores = [CONTENT_PENDING_STORE, CONTENT_ENTITIES_STORE, APP_META_STORE] as const;
  const openRead = () => db.transaction(stores, 'readonly');
  const openWrite = () => db.transaction(stores, 'readwrite');
  const readSelection = async (tx: ReturnType<typeof openRead> | ReturnType<typeof openWrite>) => {
    const pending = tx.objectStore(CONTENT_PENDING_STORE);
    const original = await pending.get([context.ownerId, captured.opId]);
    const entity = await tx.objectStore(CONTENT_ENTITIES_STORE).get(
      entityKey(context.ownerId, captured.entityType, captured.entityId),
    );
    if (!entity || entity.ownerId !== context.ownerId ||
        entity.entityType !== captured.entityType || entity.entityId !== captured.entityId ||
        entity.state === 'held' || entity.state === 'quarantined' ||
        entity.heldReason !== null || entity.quarantineReason !== null) throw mismatch();
    if (original) {
      assertOperation(original);
      if (!pendingIdentityEqual(original.tuple, captured.tuple)) throw mismatch();
      return { kind: 'in-flight' as const, operation: original };
    }
    if (captured.dispatchState !== 'pending' || !entity.pending ||
        (entity.state !== 'dirty' && entity.state !== 'tombstoned')) throw mismatch();
    const successor = await pending.get([context.ownerId, entity.pending.opId]);
    if (!successor) throw mismatch();
    assertOperation(successor);
    if (successor.dispatchState !== 'pending' ||
        successor.entityType !== captured.entityType || successor.entityId !== captured.entityId ||
        successor.opId === captured.opId ||
        !pendingIdentityEqual(entity.pending, successor.tuple) ||
        entity.mutationSeq !== successor.tuple.mutationSeq ||
        BigInt(successor.tuple.mutationSeq) <= BigInt(captured.tuple.mutationSeq)) throw mismatch();
    return { kind: 'superseded' as const, operation: successor };
  };

  // Digest verification cannot await crypto inside a live IDB transaction.
  // A second, atomic read verifies the validated bytes and the durable owner.
  // Only another valid successor can restart validation; errors never retry sync.
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const snapshotTx = openRead();
    void snapshotTx.done.catch(() => {});
    let snapshot;
    try {
      snapshot = await readSelection(snapshotTx);
      await assertContextBeforeCommit(snapshotTx, context);
      await snapshotTx.done;
    } catch (error) {
      await abortQuietly(snapshotTx);
      throw error;
    }
    await verifyDurablePendingTuple(snapshot.operation.tuple);
    assertLifecycleContext(context);
    const tx = observeQueuedWrites(openWrite());
    void tx.done.catch(() => {});
    try {
      const current = await readSelection(tx);
      if (current.kind !== snapshot.kind ||
          !pendingIdentityEqual(current.operation.tuple, snapshot.operation.tuple)) {
        // The original was never dispatched. A strictly newer successor may
        // have replaced our validation snapshot; validate that exact row next.
        if (current.kind !== 'superseded' ||
            BigInt(current.operation.tuple.mutationSeq) <= BigInt(snapshot.operation.tuple.mutationSeq)) {
          throw mismatch();
        }
        await assertContextBeforeCommit(tx, context);
        await tx.done;
        continue;
      }
      if (current.kind === 'superseded') {
        await assertContextBeforeCommit(tx, context);
        await tx.done;
        return { kind: 'superseded' };
      }
      const operation = { ...current.operation, dispatchState: 'in-flight' as const };
      await tx.objectStore(CONTENT_PENDING_STORE).put(operation);
      await assertContextBeforeCommit(tx, context);
      await tx.done;
      return { kind: 'in-flight', operation };
    } catch (error) {
      await abortQuietly(tx);
      throw error;
    }
  }
  throw new Error('content-dispatch-validation-contended');
}

export async function getDispatchableContentOperations(
  context: ContentLifecycleContext,
  enabledEntityTypes?: ReadonlySet<ContentEntityType>,
): Promise<StoredPendingContentOperationV1[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(CONTENT_PENDING_STORE, 'by-owner', context.ownerId);
  const earliest = new Map<string, StoredPendingContentOperationV1>();
  for (const operation of all) {
    if (!contextMatches(context, operation.tuple)) continue;
    if (enabledEntityTypes && !enabledEntityTypes.has(operation.entityType)) continue;
    if (operation.dispatchState !== 'pending' && operation.dispatchState !== 'in-flight') continue;
    const key = `${operation.entityType}\n${operation.entityId}`;
    const previous = earliest.get(key);
    if (!previous || BigInt(operation.tuple.mutationSeq) < BigInt(previous.tuple.mutationSeq)) {
      earliest.set(key, operation);
    }
  }
  return [...earliest.values()].sort((a, b) => a.opId.localeCompare(b.opId));
}

/**
 * Adopt same-release durable work after a verified owner returns with a new
 * auth epoch. The mutation bytes and digest stay frozen; only the local
 * dispatch lease changes. A different compiled generation is never adopted.
 */
export async function rebindPendingContentOperations(
  context: ContentLifecycleContext,
  enabledEntityTypes: ReadonlySet<ContentEntityType> = ALL_CONTENT_ENTITY_TYPE_SET,
): Promise<{ rebound: number; held: number }> {
  assertLifecycleWrite('content-lifecycle:rebind-pending');
  assertLifecycleContext(context);
  const db = await getDb();
  const tx = db.transaction(
    [CONTENT_PENDING_STORE, CONTENT_ENTITIES_STORE, APP_META_STORE],
    'readwrite',
  );
  const pendingStore = tx.objectStore(CONTENT_PENDING_STORE);
  const entityStore = tx.objectStore(CONTENT_ENTITIES_STORE);
  const operations = await pendingStore.index('by-owner').getAll(context.ownerId);
  let rebound = 0;
  let held = 0;
  for (const operation of operations) {
    if (!enabledEntityTypes.has(operation.entityType)) continue;
    if (operation.dispatchState === 'acked-superseded' || operation.dispatchState === 'rejected') {
      continue;
    }
    const key = entityKey(context.ownerId, operation.entityType, operation.entityId);
    const entity = await entityStore.get(key);
    if (!entity?.pending || !pendingIdentityEqual(entity.pending, operation.tuple)) continue;
    if (
      operation.tuple.ownerId !== context.ownerId ||
      operation.tuple.generation !== context.generation
    ) {
      void pendingStore.put({ ...operation, dispatchState: 'held' });
      void entityStore.put({
        ...entity,
        state: 'held',
        heldReason: 'awaiting-owner',
        recovery: 'held-local',
      });
      held += 1;
      continue;
    }
    if (operation.tuple.authEpoch === context.authEpoch) continue;
    const tuple = { ...operation.tuple, authEpoch: context.authEpoch };
    void pendingStore.put({ ...operation, tuple, dispatchState: 'pending' });
    void entityStore.put({
      ...entity,
      pending: tuple,
      state: entity.visibleDocument ? 'dirty' : 'tombstoned',
      heldReason: null,
      recovery: 'noop',
    });
    rebound += 1;
  }
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  if (rebound > 0) notifyContentCommitted({ ownerId: context.ownerId });
  return { rebound, held };
}

export async function acknowledgeContentReceipt(
  context: ContentLifecycleContext,
  receipt: ContentOpReceiptV1,
): Promise<boolean> {
  assertLifecycleWrite('content-lifecycle:acknowledge');
  assertLifecycleContext(context);
  await assertContentOpReceiptV1(receipt, context.ownerId);
  const envelope = receipt.envelope;
  const db = await getDb();
  const stores = envelope.entityType === 'prayer'
    ? [CONTENT_ENTITIES_STORE, CONTENT_PENDING_STORE, KEEPSAKE_FENCES_STORE, APP_META_STORE] as const
    : [CONTENT_ENTITIES_STORE, CONTENT_PENDING_STORE, APP_META_STORE] as const;
  const tx = db.transaction(stores, 'readwrite');
  const key = entityKey(context.ownerId, envelope.entityType, envelope.entityId);
  const entityStore = tx.objectStore(CONTENT_ENTITIES_STORE);
  const pendingStore = tx.objectStore(CONTENT_PENDING_STORE);
  const [entity, stored] = await Promise.all([
    entityStore.get(key),
    pendingStore.get([context.ownerId, envelope.opId]),
  ]);
  const pending = stored?.tuple ?? null;
  const passes = Boolean(
    entity &&
      pending &&
      entity.pending &&
      !entity.pendingDeleteAt &&
      contextMatches(context, pending) &&
      pendingIdentityEqual(entity.pending, pending) &&
      pending.ownerId === envelope.ownerId &&
      pending.entityType === envelope.entityType &&
      pending.entityId === envelope.entityId &&
      pending.opId === envelope.opId &&
      pending.opKind === envelope.opKind &&
      pending.expectedBaseRevision === envelope.expectedBaseRevision &&
      pending.mutationSeq === envelope.mutationSeq &&
      pending.contentDigest === envelope.contentDigest &&
      bytesEqual(pending.completePendingBytes, cj1Bytes(projectMutation(envelope))),
  );
  if (!passes || !entity || !stored) {
    await tx.done;
    return false;
  }
  assertLifecycleContext(context);
  const prayerFenceAccepted = envelope.entityType !== 'prayer' || (
    stored?.prayerFenceMutationSeq !== null &&
    stored?.prayerFenceMutationSeq !== undefined &&
    await acknowledgeLifecyclePrayerFenceInTx(
      tx as unknown as Parameters<typeof acknowledgeLifecyclePrayerFenceInTx>[0],
      envelope.entityId,
      context.ownerId,
      stored.prayerFenceMutationSeq,
    )
  );
  if (!prayerFenceAccepted) {
    await abortQuietly(tx);
    return false;
  }
  const next: LocalContentEntityV1 = {
    ...entity,
    state: envelope.payload.kind === 'live' ? 'clean' : 'tombstoned',
    installedEnvelope: envelope,
    visibleDocument: envelope.payload.kind === 'live' ? envelope.payload.document : null,
    pending: null,
    pendingDeleteAt: null,
    localBaseRevision: envelope.contentRevision,
    heldReason: null,
    quarantineReason: null,
    recovery: 'upload-acked',
  };
  void entityStore.put(next);
  void pendingStore.delete([context.ownerId, envelope.opId]);
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId, entityType: envelope.entityType, entityId: envelope.entityId });
  return true;
}

/**
 * A receipt can arrive after the user has already authored a newer local
 * successor. The receipt proves the older operation committed remotely, but
 * the strict acknowledgement verifier must not clear or replace the newer
 * tuple. Park only the older durable operation so reconnect never dispatches
 * it again; the contiguous pull still arbitrates the accepted envelope.
 */
export async function markContentReceiptSuperseded(
  context: ContentLifecycleContext,
  receipt: ContentOpReceiptV1,
): Promise<boolean> {
  assertLifecycleWrite('content-lifecycle:acknowledge-superseded');
  assertLifecycleContext(context);
  await assertContentOpReceiptV1(receipt, context.ownerId);
  const envelope = receipt.envelope;
  const db = await getDb();
  const key = entityKey(context.ownerId, envelope.entityType, envelope.entityId);
  const [snapshot, sentStored] = await Promise.all([
    db.get(CONTENT_ENTITIES_STORE, key),
    db.get(CONTENT_PENDING_STORE, [context.ownerId, envelope.opId]),
  ]);
  const sent = sentStored?.tuple ?? null;
  if (
    !snapshot ||
    !sentStored ||
    !sent ||
    !contextMatches(context, sent) ||
    !tupleMatchesEnvelope(sent, envelope)
  ) {
    return false;
  }
  const current = snapshot.pending;
  const deleteAfterAccept = Boolean(
    snapshot.pendingDeleteAt && current && pendingIdentityEqual(current, sent),
  );
  const hasNewerSuccessor = Boolean(
    current &&
      contextMatches(context, current) &&
      current.entityType === sent.entityType &&
      current.entityId === sent.entityId &&
      BigInt(current.mutationSeq) > BigInt(sent.mutationSeq),
  );
  if (!deleteAfterAccept && !hasNewerSuccessor) return false;

  const previousSuccessor = hasNewerSuccessor && current
    ? await db.get(CONTENT_PENDING_STORE, [context.ownerId, current.opId])
    : null;
  if (
    hasNewerSuccessor &&
    (!previousSuccessor ||
      !current ||
      !pendingIdentityEqual(previousSuccessor.tuple, current) ||
      (previousSuccessor.dispatchState !== 'pending' && previousSuccessor.dispatchState !== 'held'))
  ) {
    return false;
  }
  let successorMutation: ContentMutationV1;
  let successorFenceMutationSeq: string | null;
  if (deleteAfterAccept) {
    if (envelope.payload.kind !== 'live' || !snapshot.pendingDeleteAt) return false;
    successorMutation = {
      schema: 'adia-content-mutation-v1',
      protocolVersion: 1,
      ownerId: context.ownerId,
      entityType: envelope.entityType,
      entityId: envelope.entityId,
      opId: mintUuidV4(),
      opKind: 'delete',
      mutationSeq: incrementDecimalBigint(snapshot.mutationSeq),
      expectedBaseRevision: envelope.contentRevision,
      payload: {
        kind: 'tombstone-intent',
        predecessorLiveDigest: envelope.contentDigest,
        predecessorLiveSnapshot: envelope.payload.document,
        tombstonedAt: snapshot.pendingDeleteAt,
      },
    };
    successorFenceMutationSeq = sentStored.prayerFenceMutationSeq;
  } else {
    successorMutation = rebaseLocalSuccessorMutation(
      context,
      (previousSuccessor as StoredPendingContentOperationV1).mutation,
      envelope,
    );
    successorFenceMutationSeq =
      (previousSuccessor as StoredPendingContentOperationV1).prayerFenceMutationSeq;
  }
  const successorTuple = await freezeTuple(context, successorMutation);
  const successorStored: StoredPendingContentOperationV1 = {
    ownerId: context.ownerId,
    opId: successorTuple.opId,
    entityType: successorTuple.entityType,
    entityId: successorTuple.entityId,
    tuple: successorTuple,
    mutation: successorMutation,
    prayerFenceMutationSeq: successorFenceMutationSeq,
    audioQueueIds: previousSuccessor?.audioQueueIds,
    dispatchState: 'pending',
  };

  // KR8: the rebound recordings are written with a verified copy of any Blob
  // they still hold, proved here, before the transaction (see commitAuthoredMutation).
  const supersessionAudioCopies = await verifiedCaptureCopiesBeforeWrite(
    previousSuccessor?.audioQueueIds ?? [],
    context.ownerId,
  );
  // As in commitAuthoredMutation: every queued write is observed, and so is the
  // abort itself even when an awaited read rejects before the final `tx.done`
  // (for example a recording put that fails in storage, A3; a recording whose
  // bytes cannot be read is refused above, before this transaction, LC2).
  // Observation only: an abort still fails this call.
  const tx = observeQueuedWrites(db.transaction(
    [CONTENT_ENTITIES_STORE, CONTENT_PENDING_STORE, AUDIO_QUEUE_STORE, APP_META_STORE],
    'readwrite',
  ));
  void tx.done.catch(() => {});
  const [entity, stored, storedSuccessor] = await Promise.all([
    tx.objectStore(CONTENT_ENTITIES_STORE).get(key),
    tx.objectStore(CONTENT_PENDING_STORE).get([context.ownerId, envelope.opId]),
    previousSuccessor
      ? tx.objectStore(CONTENT_PENDING_STORE).get([
          context.ownerId,
          previousSuccessor.opId,
        ])
      : Promise.resolve(undefined),
  ]);
  if (
    !entitySnapshotEqual(entity, snapshot) ||
    !stored ||
    !pendingIdentityEqual(stored.tuple, sentStored.tuple) ||
    (previousSuccessor &&
      (!storedSuccessor ||
        !pendingIdentityEqual(storedSuccessor.tuple, previousSuccessor.tuple)))
  ) {
    await abortQuietly(tx);
    return false;
  }
  assertLifecycleContext(context);
  void tx.objectStore(CONTENT_PENDING_STORE).put({
    ...stored,
    dispatchState: 'acked-superseded',
  });
  if (previousSuccessor) {
    for (const queueItemId of previousSuccessor.audioQueueIds ?? []) {
      const audioRow = await tx.objectStore(AUDIO_QUEUE_STORE).get(queueItemId);
      if (
        !audioRow ||
        audioRow.ownerId !== context.ownerId ||
        audioRow.destinationOpId !== previousSuccessor.opId
      ) {
        await abortQuietly(tx);
        return false;
      }
      // Read after other writes were queued: a refusal, or any exception in
      // this added work, aborts all of them before it propagates.
      let rebound: AudioQueueRow | null = null;
      let refusal: Error | null = null;
      try {
        const candidate = keepVerifiedCaptureCopy(
          audioRow,
          { ...audioRow, destinationOpId: successorTuple.opId },
          supersessionAudioCopies.get(queueItemId),
        );
        rebound = candidate;
        refusal = captureRowTransitionRefusal(audioRow, candidate);
      } catch (error) {
        refusal = error instanceof Error ? error : new Error('capture-row-guard-failed');
      }
      if (refusal || !rebound) await abortForCaptureRefusal(tx, refusal ?? new Error('capture-row-guard-failed'));
      void tx.objectStore(AUDIO_QUEUE_STORE).put(rebound as AudioQueueRow);
    }
    void tx.objectStore(CONTENT_PENDING_STORE).delete([
      context.ownerId,
      previousSuccessor.opId,
    ]);
  }
  void tx.objectStore(CONTENT_PENDING_STORE).put(successorStored);
  void tx.objectStore(CONTENT_ENTITIES_STORE).put({
    ...entity,
    state: successorMutation.payload.kind === 'live' ? 'dirty' : 'tombstoned',
    installedEnvelope: envelope,
    visibleDocument:
      successorMutation.payload.kind === 'live' ? successorMutation.payload.document : null,
    pending: successorTuple,
    pendingDeleteAt: null,
    mutationSeq: successorTuple.mutationSeq,
    localBaseRevision: envelope.contentRevision,
    heldReason: null,
    quarantineReason: null,
    recovery: 'upload-acked',
  } satisfies LocalContentEntityV1);
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId, entityType: envelope.entityType, entityId: envelope.entityId });
  return true;
}

const DRAIN_STORES = [
  CONTENT_PARTITIONS_STORE,
  CONTENT_HOLDING_STORE,
  CONTENT_ENTITIES_STORE,
  CONTENT_CONFLICTS_STORE,
  CONTENT_STAGED_CONFLICTS_STORE,
  CONTENT_PENDING_STORE,
  CONTENT_OBSERVATIONS_STORE,
  PRAYERS_STORE,
  BIBLE_BOOKMARKS_STORE,
  BIBLE_HIGHLIGHTS_STORE,
  KEEPSAKE_FENCES_STORE,
  KEEPSAKE_META_STORE,
  APP_META_STORE,
] as const;

function openDrainTransaction(db: Awaited<ReturnType<typeof getDb>>) {
  return db.transaction(DRAIN_STORES, 'readwrite');
}

type LifecycleWriteTx = ReturnType<typeof openDrainTransaction>;

async function drainContiguousInTransaction(
  tx: LifecycleWriteTx,
  context: ContentLifecycleContext,
  partition: ContentPartitionV1,
  installedAt: string,
  projectedEntityTypes: ReadonlySet<ContentEntityType>,
): Promise<ContentPartitionV1> {
  const holding = tx.objectStore(CONTENT_HOLDING_STORE);
  const entities = tx.objectStore(CONTENT_ENTITIES_STORE);
  const conflicts = tx.objectStore(CONTENT_CONFLICTS_STORE);
  const staged = tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE);
  const pendingStore = tx.objectStore(CONTENT_PENDING_STORE);
  const fenceFinalizers: Array<{
    prayerId: string;
    marks: { seq: string; epoch: string };
  }> = [];
  let nextPartition = { ...partition };
  while (true) {
    const revision = nextPartition.frontier + 1;
    const envelope = await holding.get([context.ownerId, revision]);
    if (!envelope) break;
    const key = entityKey(context.ownerId, envelope.entityType, envelope.entityId);
    const [prior, activeConflict, stagedConflict, acceptedPredecessor] = await Promise.all([
      entities.get(key),
      conflicts.get(key),
      staged.get(key),
      pendingStore.get([context.ownerId, envelope.opId]),
    ]);
    const isAcceptedLocalPredecessor = Boolean(
      prior?.pending &&
      !activeConflict &&
      !stagedConflict &&
      acceptedPredecessor?.dispatchState === 'acked-superseded' &&
      tupleMatchesEnvelope(acceptedPredecessor.tuple, envelope),
    );
    const result = isAcceptedLocalPredecessor
      ? {
          entity: {
            ...prior,
            state: prior.visibleDocument ? 'dirty' as const : 'tombstoned' as const,
            installedEnvelope: envelope,
            localBaseRevision: envelope.contentRevision,
            heldReason: null,
            quarantineReason: null,
            recovery: 'upload-acked' as const,
          },
          conflict: null,
        }
      : installContiguousEnvelope(
          prior ?? null,
          envelope,
          activeConflict ?? null,
          stagedConflict ?? null,
          installedAt,
        );
    void entities.put(result.entity);
    if (isAcceptedLocalPredecessor || !projectedEntityTypes.has(envelope.entityType)) {
      // The visible projection already contains the newer local successor.
      // Installing the accepted predecessor over it would cause a UI rollback.
    } else if (envelope.payload.kind === 'live') {
      await putLiveProjection(
        tx as unknown as Parameters<typeof putLiveProjection>[0],
        envelope.entityType,
        envelope.payload.document,
      );
    } else {
      await putTombstoneProjection(
        tx as unknown as Parameters<typeof putTombstoneProjection>[0],
        envelope.entityType,
        envelope.entityId,
        envelope.payload.tombstonedAt,
      );
    }
    if (
      envelope.entityType === 'prayer' &&
      projectedEntityTypes.has('prayer') &&
      !isAcceptedLocalPredecessor &&
      !(
        prior?.pending?.opId === envelope.opId &&
        prior.pending.contentDigest === envelope.contentDigest &&
        bytesEqual(prior.pending.completePendingBytes, cj1Bytes(projectMutation(envelope)))
      ) &&
      (prior?.installedEnvelope?.contentRevision !== envelope.contentRevision ||
        prior.installedEnvelope.contentDigest !== envelope.contentDigest)
    ) {
      const marks = await bumpLifecyclePrayerFenceInTx(
        tx as unknown as Parameters<typeof bumpLifecyclePrayerFenceInTx>[0],
        envelope.entityId,
        context.ownerId,
        'pulled',
      );
      fenceFinalizers.push({ prayerId: envelope.entityId, marks });
    }
    if (result.conflict) void conflicts.put(result.conflict);
    if (stagedConflict) {
      void staged.delete(key);
      void pendingStore.delete([context.ownerId, stagedConflict.rejectedOpId]);
      if (stagedConflict.supersedingMutation) {
        void pendingStore.delete([context.ownerId, stagedConflict.supersedingMutation.opId]);
      }
    } else if (isAcceptedLocalPredecessor) {
      void pendingStore.delete([context.ownerId, envelope.opId]);
    } else if (prior?.pending && result.entity.pending === null) {
      void pendingStore.delete([context.ownerId, prior.pending.opId]);
    }
    void holding.delete([context.ownerId, revision]);
    nextPartition = {
      ...nextPartition,
      frontier: revision,
      heldReason: null,
      quarantineReason: null,
    };
  }
  for (const finalizer of fenceFinalizers) {
    // Queue the callbacks on the transaction object; the public callers run
    // them only after tx.done so broadcasts/digest work never escape a failed
    // atomic install.
    const carrier = tx as unknown as {
      __contentFenceFinalizers?: typeof fenceFinalizers;
    };
    (carrier.__contentFenceFinalizers ??= []).push(finalizer);
  }
  return nextPartition;
}

function finalizeDrainFences(tx: LifecycleWriteTx): void {
  const carrier = tx as unknown as {
    __contentFenceFinalizers?: Array<{
      prayerId: string;
      marks: { seq: string; epoch: string };
    }>;
  };
  for (const item of carrier.__contentFenceFinalizers ?? []) {
    finalizeLifecyclePrayerFence(item.prayerId, item.marks);
  }
}

export async function stageContentEnvelopes(
  context: ContentLifecycleContext,
  rawEnvelopes: unknown[],
  observedHighWater: number,
  observedAt: string,
  projectedEntityTypes: ReadonlySet<ContentEntityType> = ALL_CONTENT_ENTITY_TYPE_SET,
): Promise<ContentPartitionV1> {
  assertLifecycleWrite('content-lifecycle:stage-and-drain');
  assertLifecycleContext(context);
  const recognized: OrdinaryContentEnvelopeV1[] = [];
  for (const raw of rawEnvelopes) {
    const classification = await classifyOrdinaryEnvelope(raw, context.ownerId);
    if (classification.kind !== 'recognized') {
      const db = await getDb();
      const tx = db.transaction(
        [CONTENT_PARTITIONS_STORE, CONTENT_OBSERVATIONS_STORE, APP_META_STORE],
        'readwrite',
      );
      const partitions = tx.objectStore(CONTENT_PARTITIONS_STORE);
      const current = (await partitions.get(context.ownerId)) ?? defaultPartition(context.ownerId);
      const reason =
        classification.kind === 'unsupported'
          ? 'unknown-content-protocol'
          : classification.reason;
      void tx.objectStore(CONTENT_OBSERVATIONS_STORE).put({
        ownerId: context.ownerId,
        observationKey: `${observedAt}:${crypto.randomUUID()}`,
        reason,
        raw,
        observedAt,
      });
      void partitions.put({
        ...current,
        heldReason: reason === 'unknown-content-protocol' ? reason : current.heldReason,
        quarantineReason: reason === 'unknown-content-protocol' ? null : reason,
      });
      await assertContextBeforeCommit(tx, context);
      await tx.done;
      return (await getContentPartition(context.ownerId));
    }
    recognized.push(classification.envelope);
  }

  const db = await getDb();
  assertLifecycleContext(context);
  const tx = openDrainTransaction(db);
  const partitions = tx.objectStore(CONTENT_PARTITIONS_STORE);
  const holding = tx.objectStore(CONTENT_HOLDING_STORE);
  const entities = tx.objectStore(CONTENT_ENTITIES_STORE);
  const observations = tx.objectStore(CONTENT_OBSERVATIONS_STORE);
  const current = (await partitions.get(context.ownerId)) ?? defaultPartition(context.ownerId);
  if (
    !Number.isSafeInteger(observedHighWater) ||
    observedHighWater < current.lastObservedHighWater ||
    recognized.some((envelope) => envelope.contentRevision > observedHighWater)
  ) {
    await abortQuietly(tx);
    throw new Error('content-high-water-malformed');
  }
  for (const envelope of recognized) {
    const key = [context.ownerId, envelope.contentRevision] as [string, number];
    if (envelope.contentRevision <= current.frontier) {
      const installed = await entities.get(
        entityKey(context.ownerId, envelope.entityType, envelope.entityId),
      );
      if (
        installed?.installedEnvelope?.contentRevision === envelope.contentRevision &&
        envelopeBytesEqual(installed.installedEnvelope, envelope)
      ) {
        continue;
      }
      const quarantined: ContentPartitionV1 = {
        ...current,
        lastObservedHighWater: observedHighWater,
        quarantineReason: 'digest-mismatch',
      };
      if (installed?.installedEnvelope?.contentRevision === envelope.contentRevision) {
        void entities.put({
          ...installed,
          state: 'quarantined',
          quarantineReason: 'digest-mismatch',
          recovery: 'quarantined',
        });
      }
      void observations.put({
        ownerId: context.ownerId,
        observationKey:
          `${observedAt}:revision-${envelope.contentRevision}:${crypto.randomUUID()}`,
        reason: 'digest-mismatch',
        raw: envelope,
        observedAt,
      });
      void partitions.put(quarantined);
      await assertContextBeforeCommit(tx, context);
      await tx.done;
      return quarantined;
    }
    const existing = await holding.get(key);
    if (existing && !envelopeBytesEqual(existing, envelope)) {
      const quarantined: ContentPartitionV1 = {
        ...current,
        lastObservedHighWater: observedHighWater,
        quarantineReason: 'digest-mismatch',
      };
      void observations.put({
        ownerId: context.ownerId,
        observationKey:
          `${observedAt}:revision-${envelope.contentRevision}:${crypto.randomUUID()}`,
        reason: 'digest-mismatch',
        raw: envelope,
        observedAt,
      });
      void partitions.put(quarantined);
      await assertContextBeforeCommit(tx, context);
      await tx.done;
      return quarantined;
    }
    if (!existing && envelope.contentRevision > current.frontier) void holding.put(envelope);
  }
  let next = await drainContiguousInTransaction(
    tx as unknown as LifecycleWriteTx,
    context,
    { ...current, lastObservedHighWater: observedHighWater },
    observedAt,
    projectedEntityTypes,
  );
  const remaining = await holding.getAll(IDBKeyRange.bound(
    [context.ownerId, next.frontier + 1],
    [context.ownerId, Number.MAX_SAFE_INTEGER],
  ));
  if (remaining.length > 0) next = { ...next, heldReason: 'incomplete-log' };
  // Observe this request before the context check can abort its transaction.
  try {
    await partitions.put(next);
  } catch (error) {
    try {
      await abortQuietly(tx);
    } catch (rollbackError) {
      throw new AggregateError([error, rollbackError], 'content-partition-write-rollback-failed');
    }
    throw error;
  }
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId });
  finalizeDrainFences(tx as unknown as LifecycleWriteTx);
  return next;
}

export async function handleContentCasRejection(
  context: ContentLifecycleContext,
  sent: DurablePendingContentTupleV1,
  rawWinner: unknown,
  observedAt: string,
): Promise<'staged' | 'installed' | 'ignored'> {
  assertLifecycleWrite('content-lifecycle:cas-rejection');
  assertLifecycleContext(context);
  await verifyDurablePendingTuple(sent);
  const classified = await classifyOrdinaryEnvelope(rawWinner, context.ownerId);
  if (classified.kind !== 'recognized') {
    await stageContentEnvelopes(context, [rawWinner], 0, observedAt);
    return 'ignored';
  }
  const winner = classified.envelope;
  assertLifecycleContext(context);
  const db = await getDb();
  const tx = openDrainTransaction(db);
  const key = entityKey(context.ownerId, sent.entityType, sent.entityId);
  const [entity, sentStored, partitionRow, existingHolding, existingStaged, activeConflict] = await Promise.all([
    tx.objectStore(CONTENT_ENTITIES_STORE).get(key),
    tx.objectStore(CONTENT_PENDING_STORE).get([context.ownerId, sent.opId]),
    tx.objectStore(CONTENT_PARTITIONS_STORE).get(context.ownerId),
    tx.objectStore(CONTENT_HOLDING_STORE).get([context.ownerId, winner.contentRevision]),
    tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).get(key),
    tx.objectStore(CONTENT_CONFLICTS_STORE).get(key),
  ]);
  const current = entity?.pending ?? null;
  const sentExact = sentStored && pendingIdentityEqual(sentStored.tuple, sent);
  const currentIsSent = current && pendingIdentityEqual(current, sent);
  const currentIsNewer = Boolean(
    current &&
      contextMatches(context, current) &&
      current.entityType === sent.entityType &&
      current.entityId === sent.entityId &&
      BigInt(current.mutationSeq) > BigInt(sent.mutationSeq),
  );
  if (!sentExact || !entity || !contextMatches(context, sent) || (!currentIsSent && !currentIsNewer)) {
    void tx.objectStore(CONTENT_OBSERVATIONS_STORE).put({
      ownerId: context.ownerId,
      observationKey: `${observedAt}:${sent.opId}`,
      reason: 'unverifiable-owner',
      raw: rawWinner,
      observedAt,
    });
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    return 'ignored';
  }
  const partition = partitionRow ?? defaultPartition(context.ownerId);
  const winnerBoundToSent =
    winner.entityType === sent.entityType && winner.entityId === sent.entityId;
  const holdingCollision = Boolean(
    existingHolding && !envelopeBytesEqual(existingHolding, winner),
  );
  if (!winnerBoundToSent || holdingCollision) {
    const reason = winnerBoundToSent ? 'digest-mismatch' : 'malformed-envelope';
    void tx.objectStore(CONTENT_OBSERVATIONS_STORE).put({
      ownerId: context.ownerId,
      observationKey: `${observedAt}:${sent.opId}:cas-winner`,
      reason,
      raw: rawWinner,
      observedAt,
    });
    void tx.objectStore(CONTENT_PARTITIONS_STORE).put({
      ...partition,
      quarantineReason: reason,
    });
    void tx.objectStore(CONTENT_ENTITIES_STORE).put({
      ...entity,
      state: 'quarantined',
      quarantineReason: reason,
      recovery: 'quarantined',
    });
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    return 'ignored';
  }
  const rejectedMutation = mutationFromPending(sent);
  const supersedingMutation = currentIsNewer && current ? mutationFromPending(current) : null;
  const stagedConflict: StagedConflictV1 = {
    recordVersion: 1,
    ownerId: context.ownerId,
    authEpoch: context.authEpoch,
    generation: context.generation,
    entityType: sent.entityType,
    entityId: sent.entityId,
    heldReason: 'awaiting-contiguous-winner',
    winningContentRevision: winner.contentRevision,
    winningContentDigest: winner.contentDigest,
    rejectedMutation,
    rejectedContentDigest: sent.contentDigest,
    supersedingMutation,
    supersedingContentDigest: currentIsNewer && current ? current.contentDigest : null,
    rejectedExpectedBaseRevision: sent.expectedBaseRevision,
    rejectedOpId: sent.opId,
    rejectedMutationSeq: sent.mutationSeq,
    observedAt,
  };
  const stagedCollision = Boolean(
    existingStaged &&
      !bytesEqual(
        cj1Bytes(existingStaged),
        cj1Bytes({ ...stagedConflict, observedAt: existingStaged.observedAt }),
      ),
  );
  if (stagedCollision) {
    void tx.objectStore(CONTENT_OBSERVATIONS_STORE).put({
      ownerId: context.ownerId,
      observationKey: `${observedAt}:${sent.opId}:staged-conflict`,
      reason: 'digest-mismatch',
      raw: rawWinner,
      observedAt,
    });
    void tx.objectStore(CONTENT_PARTITIONS_STORE).put({
      ...partition,
      quarantineReason: 'digest-mismatch',
    });
    void tx.objectStore(CONTENT_ENTITIES_STORE).put({
      ...entity,
      state: 'quarantined',
      quarantineReason: 'digest-mismatch',
      recovery: 'quarantined',
    });
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    return 'ignored';
  }
  const pendingStore = tx.objectStore(CONTENT_PENDING_STORE);
  void pendingStore.put({ ...sentStored, dispatchState: 'rejected' });
  if (currentIsNewer && current) {
    const successor = await pendingStore.get([context.ownerId, current.opId]);
    if (successor) void pendingStore.put({ ...successor, dispatchState: 'held' });
  }
  if (!existingHolding && winner.contentRevision > partition.frontier) {
    void tx.objectStore(CONTENT_HOLDING_STORE).put(winner);
  }
  if (!existingStaged) void tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).put(stagedConflict);
  void tx.objectStore(CONTENT_ENTITIES_STORE).put({
    ...entity,
    state: 'held',
    heldReason: 'awaiting-contiguous-winner',
    recovery: 'gap-held',
  });
  if (winner.contentRevision <= partition.frontier) {
    if (
      entity.installedEnvelope?.contentRevision !== winner.contentRevision ||
      !envelopeBytesEqual(entity.installedEnvelope, winner)
    ) {
      void tx.objectStore(CONTENT_OBSERVATIONS_STORE).put({
        ownerId: context.ownerId,
        observationKey: `${observedAt}:${sent.opId}:installed-winner`,
        reason: 'digest-mismatch',
        raw: rawWinner,
        observedAt,
      });
      void tx.objectStore(CONTENT_PARTITIONS_STORE).put({
        ...partition,
        quarantineReason: 'digest-mismatch',
      });
      void tx.objectStore(CONTENT_ENTITIES_STORE).put({
        ...entity,
        state: 'quarantined',
        quarantineReason: 'digest-mismatch',
        recovery: 'quarantined',
      });
      await assertContextBeforeCommit(tx, context);
      await tx.done;
      return 'ignored';
    }
    const result = installContiguousEnvelope(
      entity,
      winner,
      activeConflict ?? null,
      existingStaged ?? stagedConflict,
      observedAt,
    );
    void tx.objectStore(CONTENT_ENTITIES_STORE).put(result.entity);
    if (result.conflict) void tx.objectStore(CONTENT_CONFLICTS_STORE).put(result.conflict);
    void tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).delete(key);
    void pendingStore.delete([context.ownerId, sent.opId]);
    if (currentIsNewer && current) {
      void pendingStore.delete([context.ownerId, current.opId]);
    }
    void tx.objectStore(CONTENT_PARTITIONS_STORE).put(partition);
    await assertContextBeforeCommit(tx, context);
    await tx.done;
    notifyContentCommitted({ ownerId: context.ownerId, entityType: sent.entityType, entityId: sent.entityId });
    return 'installed';
  }
  let nextPartition = await drainContiguousInTransaction(
    tx as unknown as LifecycleWriteTx,
    context,
    {
      ...partition,
      lastObservedHighWater: Math.max(partition.lastObservedHighWater, winner.contentRevision),
    },
    observedAt,
    new Set([winner.entityType]),
  );
  const installed = nextPartition.frontier >= winner.contentRevision;
  if (!installed) nextPartition = { ...nextPartition, heldReason: 'incomplete-log' };
  void tx.objectStore(CONTENT_PARTITIONS_STORE).put(nextPartition);
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId });
  finalizeDrainFences(tx as unknown as LifecycleWriteTx);
  return installed ? 'installed' : 'staged';
}

export async function keepRemoteConflict(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  entityId: string,
): Promise<void> {
  assertLifecycleWrite('content-lifecycle:keep-remote');
  assertLifecycleContext(context);
  const db = await getDb();
  const stores = [CONTENT_ENTITIES_STORE, CONTENT_CONFLICTS_STORE, APP_META_STORE] as const;
  const tx = db.transaction(stores, 'readwrite');
  const key = entityKey(context.ownerId, entityType, entityId);
  const [entity, conflict] = await Promise.all([
    tx.objectStore(CONTENT_ENTITIES_STORE).get(key),
    tx.objectStore(CONTENT_CONFLICTS_STORE).get(key),
  ]);
  if (
    !entity ||
    !conflict ||
    entity.installedEnvelope?.contentRevision !== conflict.winningContentRevision ||
    entity.installedEnvelope.contentDigest !== conflict.winningContentDigest
  ) {
    await abortQuietly(tx);
    throw new Error('content-conflict-winner-changed');
  }
  void tx.objectStore(CONTENT_CONFLICTS_STORE).delete(key);
  void tx.objectStore(CONTENT_ENTITIES_STORE).put({
    ...entity,
    state: entity.installedEnvelope.payload.kind === 'live' ? 'clean' : 'tombstoned',
    recovery: 'noop',
  });
  await assertContextBeforeCommit(tx, context);
  await tx.done;
  notifyContentCommitted({ ownerId: context.ownerId });
}

export async function restoreConflictLoser(
  context: ContentLifecycleContext,
  entityType: ContentEntityType,
  entityId: string,
): Promise<DurablePendingContentTupleV1 | null> {
  assertLifecycleWrite('content-lifecycle:restore-loser');
  assertLifecycleContext(context);
  for (let attempt = 0; attempt < MAX_AUTHOR_RETRIES; attempt += 1) {
    const db = await getDb();
    const key = entityKey(context.ownerId, entityType, entityId);
    const [entity, conflict] = await Promise.all([
      db.get(CONTENT_ENTITIES_STORE, key),
      db.get(CONTENT_CONFLICTS_STORE, key),
    ]);
    if (!entity || !conflict || !entity.installedEnvelope) {
      throw new Error('content-conflict-not-found');
    }
    const operation = restoreLoserOperationKind(conflict);
    if (operation === 'keep-remote') {
      await keepRemoteConflict(context, entityType, entityId);
      return null;
    }
    const mutationSeq = incrementDecimalBigint(entity.mutationSeq);
    const opId = mintUuidV4();
    let mutation: ContentMutationV1;
    let visible: CompleteLiveDocument | null;
    if (operation === 'delete') {
      if (entity.installedEnvelope.payload.kind !== 'live') {
        throw new Error('content-delete-resolution-requires-live-winner');
      }
      mutation = {
        schema: 'adia-content-mutation-v1',
        protocolVersion: 1,
        ownerId: context.ownerId,
        entityType,
        entityId,
        opId,
        opKind: 'delete',
        mutationSeq,
        expectedBaseRevision: conflict.winningContentRevision,
        payload: {
          kind: 'tombstone-intent',
          predecessorLiveDigest: entity.installedEnvelope.contentDigest,
          predecessorLiveSnapshot: entity.installedEnvelope.payload.document,
          tombstonedAt:
            conflict.losingPayload.kind === 'tombstone'
              ? conflict.losingPayload.tombstonedAt
              : conflict.losingPayload.kind === 'tombstone-intent'
                ? conflict.losingPayload.tombstonedAt
                : new Date().toISOString(),
        },
      };
      visible = null;
    } else {
      if (conflict.losingPayload.kind !== 'live') {
        throw new Error('content-live-resolution-requires-live-loser');
      }
      mutation = buildLiveMutation(
        context,
        entityType,
        conflict.losingPayload.document,
        operation,
        mutationSeq,
        conflict.winningContentRevision,
        opId,
      );
      visible = conflict.losingPayload.document;
    }
    const tuple = await freezeTuple(context, mutation);
    if (await commitAuthoredMutation(context, entity, mutation, tuple, visible, true)) return tuple;
  }
  throw new Error('content-resolution-retry-exhausted');
}

export function contentOpKindForPending(tuple: DurablePendingContentTupleV1): ContentOpKind {
  return tuple.opKind;
}
